<script setup>
import { computed, defineEmits, defineProps, ref, watch } from "vue";
import UtilDateTime from "@/utils/UtilDateTime";
import ERP from "@/api/v2/ERP.mjs";
import View from "@/api/v2/View.mjs";
import EntityService from "@/services/entityService";
import {
  formatarKv,
  formatarNumeroInput,
  recalcularCamposComerciaisItem,
  calcularImpostosVendaItem,
  itemEhServico,
  criarItemPadrao,
  TIPOS_ORCAMENTO_VENDA,
  TIPOS_DE_ORCAMENTO
} from "../services/solicitacaoDeOrcamentoRules";

const props = defineProps({
  formularioAberto: { type: Boolean, default: false },
  modoFormulario: { type: String, default: "create" },
  formulario: { type: Object, required: true },
  statusOpcoes: { type: Array, default: () => [] },
  clientes: { type: Array, default: () => [] },
  clientesCarregando: { type: Boolean, default: false },
  clienteBusca: { type: String, default: "" },
  contatosCliente: { type: Array, default: () => [] },
  vendedores: { type: Array, default: () => [] },
  orcamentistas: { type: Array, default: () => [] },
  contatosComerciaisCliente: { type: Array, default: () => [] },
  contatosTecnicosCliente: { type: Array, default: () => [] },
  tiposDeEquipamento: { type: Array, default: () => [] },
  niveisProposta: { type: Array, default: () => [] },
  abrirNovoContato: { type: Boolean, default: false },
  novoContato: {
    type: Object,
    default: () => ({ nome: "", email: "", telefone: "", celular: "" })
  },
  followUps: { type: Array, default: () => [] },
  revisoes: { type: Array, default: () => [] },
  // Passo 3 do plano de performance (docs/modulos/lentidao-solicitacao-de-orcamento.md):
  // controla o botão manual de busca de histórico completo (fallback de até 2000 registros
  // por schema), que não roda mais sozinho ao abrir a tela.
  revisoesFallbackDisponivel: { type: Boolean, default: false },
  revisoesFallbackCarregando: { type: Boolean, default: false },
  carregando: { type: Boolean, default: false },
  somenteLeitura: { type: Boolean, default: false },
  camposBloqueados: { type: Boolean, default: false },
  statusConcluido: { type: Boolean, default: false },
  hasJobAssociado: { type: Boolean, default: false },
  podeGerarJob: { type: Boolean, default: true },
  podeConcluirSolicitacao: { type: Boolean, default: false }
});

const emits = defineEmits([
  "update:formularioAberto",
  "fechar",
  "gerar-capa-proposta",
  "salvar",
  "concluir-solicitacao",
  "buscar-clientes",
  "normalizar-numericos",
  "selecionar-cliente",
  "selecionar-contato",
  "selecionar-contato-tecnico",
  "selecionar-tipo-equipamento",
  "alterar-empresa",
  "criar-contato",
  "update:abrirNovoContato",
  "novo-followup",
  "editar-followup",
  "deletar-followup",
  "gerar-revisao",
  "buscar-historico-completo",
  "gerar-job",
  "add-item",
  "update-item",
  "remove-item",
  "normalizar-item",
  "recalcular-itens-tipo-venda"
]);

const dialogModel = computed({
  get: () => props.formularioAberto,
  set: (valor) => emits("update:formularioAberto", valor)
});

const abrirNovoContatoModel = computed({
  get: () => props.abrirNovoContato,
  set: (valor) => emits("update:abrirNovoContato", valor)
});

const bloqueiaAcoesEdicao = computed(() => {
  const textoStatus = normalizarTexto(props.formulario?.statusDescription || props.formulario?.status || "");
  return textoStatus.includes("CANCEL") || textoStatus.includes("PEDIDO COLOCADO");
});

const formularioBloqueado = computed(() => props.somenteLeitura || props.camposBloqueados);

const podeEditarCabecalho = computed(() => !props.somenteLeitura && !bloqueiaAcoesEdicao.value);

const panelCliente = ref([0]);
const panelTecnica = ref([0]);
const panelComercial = ref([0]);
const panelItens = ref([0]);
const panelObservacoes = ref([0]);
const panelFollowUp = ref([0]);
const panelRevisoes = ref([0]);

const tipoContatoDialog = ref("comercial");
const opcoesEmpresa = ["DELTA DUCON", "POWDER"];

// eslint-disable-next-line no-unused-vars
const tipoEquipamentoItemText = (item) => [
  item?.sigla || item?.codigo || item?.code,
  item?.descricao || item?.nome || item?.description || item?.name
].filter(Boolean).join(" – ");
const tipoEquipamentoFilter = (item, queryText) => {
  const texto = `${item?.sigla || item?.codigo || item?.code || ""} ${item?.descricao || item?.nome || item?.description || item?.name || ""}`.toLowerCase();
  return texto.includes((queryText || "").toLowerCase());
};
const nivelPropostaItemText = (item) => `${item?.numero ?? ""} - ${item?.descricao ?? ""}`;

const tiposDeEquipamentoFiltrados = computed(() => {
  const lista = Array.isArray(props.tiposDeEquipamento) ? props.tiposDeEquipamento : [];
  const origem = String(props.formulario?.origem || "").toUpperCase();
  const prefixo = origem.includes("POWDER") ? "P" : "D";

  return lista.filter((item) => {
    const sigla = String(item?.sigla || item?.codigo || item?.code || "").toUpperCase();
    return sigla.startsWith(prefixo);
  });
});

const clienteItemText = (item) => {
  const codigo = item?.codigo || item?.code || item?.id || "-";
  const nome = item?.nomeFantasia || item?.razaoSocial || item?.nome || "-";
  return `${codigo} - ${nome}`;
};

// Items helpers
const hasItens = computed(() => Array.isArray(props.formulario?.itens) && props.formulario.itens.length > 0);

// Controla exibição da seção agregada de serviço (ISS/IRRF/CSLL/INSS) na capa
const hasItensServico = computed(() => (
  (props.formulario?.itens || []).some((item) => itemEhServico(item))
));

// Troca de Tipo de Venda com itens ja cadastrados exige confirmacao, pois recalcula os itens
const dialogConfirmarTrocaTipoVenda = ref(false);
const tipoOrcamentoVendaPendente = ref(null);

const tipoOrcamentoVendaModel = computed({
  get: () => props.formulario?.tipoOrcamentoVenda,
  set: (valor) => {
    if (valor === props.formulario.tipoOrcamentoVenda) return;

    if (hasItens.value) {
      tipoOrcamentoVendaPendente.value = valor;
      dialogConfirmarTrocaTipoVenda.value = true;
      return;
    }

    props.formulario.tipoOrcamentoVenda = valor;
  }
});

function confirmarTrocaTipoVenda() {
  const novoTipo = tipoOrcamentoVendaPendente.value;
  props.formulario.tipoOrcamentoVenda = novoTipo;
  emits("recalcular-itens-tipo-venda", novoTipo);
  tipoOrcamentoVendaPendente.value = null;
  dialogConfirmarTrocaTipoVenda.value = false;
}

function cancelarTrocaTipoVenda() {
  tipoOrcamentoVendaPendente.value = null;
  dialogConfirmarTrocaTipoVenda.value = false;
}

const camposComercialReadonly = computed(() => true);

function parseNumeroLocal(valor) {
  if (typeof valor === "number") return Number.isFinite(valor) ? valor : 0;
  if (!valor) return 0;
  const texto = String(valor).trim().replace(/\s/g, "").replace(/(\d)\.(?=\d{3}(\D|$))/g, "$1").replace(",", ".");
  const numero = Number(texto);
  return Number.isFinite(numero) ? numero : 0;
}

// Todo (Solicitação de Orçamento - Regras de Preenchimento de Campos de Impostos): nenhum campo de
// porcentagem/basePorcentagem pode ficar abaixo de 0% ou acima de 100%. `regraPercentual` alimenta o
// `:rules` de cada v-text-field percentual (feedback visual imediato); `percentualForaDoIntervalo` +
// as listas abaixo são usados em confirmarNovoItem() pra efetivamente bloquear o "Adicionar" enquanto
// algum percentual relevante ao tipo do item (produto x serviço) estiver fora da faixa.
function regraPercentual(valor) {
  const numero = parseNumeroLocal(valor);
  return (numero >= 0 && numero <= 100) || "Deve estar entre 0% e 100%";
}

function percentualForaDoIntervalo(valor) {
  const numero = parseNumeroLocal(valor);
  return !(numero >= 0 && numero <= 100);
}

const CAMPOS_PERCENTUAL_PRODUTO = ["porcentagemICMSVenda", "pocentagemIPIVenda", "porcentagemBaseCalculoICMS"];
const CAMPOS_PERCENTUAL_SERVICO = [
  "porcentagemISS", "porcentagemBaseCalculoISS",
  "porcentagemIRRF", "porcentagemBaseCalculoIRRF",
  "porcentagemPIS", "porcentagemBaseCalculoPIS",
  "porcentagemCOFINS", "porcentagemBaseCalculoCOFINS",
  "porcentagemCSLL", "porcentagemBaseCalculoCSLL",
  "porcentagemINSS", "porcentagemBaseCalculoINSS"
];

function calcularValorTotalItem(item) {
  const quantidade = parseNumeroLocal(item.quantidade);
  const preco = parseNumeroLocal(item.precoVenda_sIPI_cICMS);
  return quantidade * preco;
}

function calcularValorDisponivelTotalItem(item) {
  const quantidade = parseNumeroLocal(item.quantidade);
  const valorDisponivel = parseNumeroLocal(item.valorDisponivel);
  return quantidade * valorDisponivel;
}

function formatarMoeda(valor) {
  return Number(parseNumeroLocal(valor)).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}

function formatarValorTotal(item) {
  const val = calcularValorTotalItem(item);
  return formatarMoeda(val);
}

function formatarValorDisponivelTotal(item) {
  return formatarMoeda(calcularValorDisponivelTotalItem(item || {}));
}

function formatarValorNumero(valor) {
  return formatarNumeroInput(parseNumeroLocal(valor));
}

function formatarValorDisponivelTotalNumero(item) {
  return formatarValorNumero(calcularValorDisponivelTotalItem(item || {}));
}

function formatarKiNumero(valor) {
  return formatarNumeroInput(parseNumeroLocal(valor), 3);
}

function formatarKvNumero(valor) {
  return formatarKv(valor ?? 0);
}

// A pedido do usuário: colunas %ISS / %ICMS / %IPI do grid de itens exibem o valor
// já com o "%" ao lado (ex.: "1,65" -> "1,65%"), sem alterar o valor armazenado no item.
function formatarPercentualGrid(valor) {
  const texto = valor === undefined || valor === null || valor === "" ? "0,00" : String(valor);
  return `${texto}%`;
}

function formatarValorTotalNumero(item) {
  const valorTotal = item?.vlrTotal || item?.valorTotal || calcularValorTotalItem(item);
  return formatarValorNumero(valorTotal);
}

function formatarMoedaDigitacao(valor) {
  const apenasDigitos = String(valor || "").replace(/\D/g, "");
  const centavos = Number(apenasDigitos || 0);
  return Number(centavos / 100).toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}

function aoDigitarMoedaNovoItem(campo, valor) {
  novoItemForm.value[campo] = formatarMoedaDigitacao(valor);
  recalcularNovoItemForm();
}

// BUGFIX (a pedido do usuário): o campo KI aceitava o texto digitado (ou um valor vindo de outro
// registro) sem reformatar, então podia ficar exibido com ponto decimal (ex.: "0.5") em vez de vírgula
// ("0,500"), inconsistente com o resto da tela (pt-BR). Reformata ao perder o foco, sempre com vírgula.
function aoSairDoKi() {
  novoItemForm.value.ki = formatarKiNumero(novoItemForm.value.ki);
}

// BUGFIX (a pedido do usuário): campos de porcentagem (e o KI) aceitavam qualquer caractere digitado —
// letras incluídas —, contando só com a mensagem de erro de regraPercentual pra avisar depois. Agora a
// digitação em si já bloqueia tudo que não for dígito ou separador decimal. O "." é convertido para ","
// na mesma posição em que foi digitado (não é um reformate do valor inteiro, só uma troca de caractere),
// mantendo o padrão pt-BR usado no resto da tela; e só a primeira vírgula digitada é mantida.
function sanitizarPercentualDigitado(valor) {
  if (typeof valor !== "string") return valor;
  const semLetras = valor.replace(/\./g, ",").replace(/[^0-9,]/g, "");
  const primeiraVirgula = semLetras.indexOf(",");
  if (primeiraVirgula === -1) return semLetras;
  return (
    semLetras.slice(0, primeiraVirgula + 1) +
    semLetras.slice(primeiraVirgula + 1).replace(/,/g, "")
  );
}

function aoDigitarPercentual(campo, valor) {
  novoItemForm.value[campo] = sanitizarPercentualDigitado(valor);
  recalcularNovoItemForm();
}

// BUGFIX (a pedido do usuário): parseNumeroLocal/parseNumero (usados no resto da tela) tratam "." como
// separador de milhar quando há exatamente 3 dígitos depois dele — regra certa para preços, mas errada
// para o KI, que é um multiplicador pequeno (ex.: 0,7 / 1,05) e nunca chega à casa dos milhares. Isso
// fazia "0.578" virar 578 (interpretado como "0.578" -> milhar "578") e ser exibido como "578,000" em
// vez de "0,578". Ao digitar, o "." é sempre tratado como separador decimal (mesma sanitização usada
// nos campos de porcentagem, que também bloqueia letras).
function aoDigitarKi(valor) {
  novoItemForm.value.ki = sanitizarPercentualDigitado(valor);
  recalcularNovoItemForm();
}

function obterIndexItem(item) {
  return (props.formulario?.itens || []).indexOf(item);
}

const dialogFollowUp = ref(false);
const followUpForm = ref({ description: "", data: "", contato_comercial: "", status: null });
const followUpErros = ref({ description: "", status: "", contato_comercial: "" });
const dialogConfirmarFechamentoCancelado = ref(false);
const dialogConfirmarConclusao = ref(false);
const dialogSelecionarTipoRevisao = ref(false);
const tipoRevisaoSelecionado = ref(null);
const dialogNovoItem = ref(false);
const indiceItemEmEdicao = ref(null);
const itensTiposDeEquipamento = ref([]);
const itensTiposDeEquipamentoCarregando = ref(false);
const itensTiposDeServico = ref([]);
const itensTiposDeServicoCarregando = ref(false);
const itensNcm = ref([]);
const itensNcmCarregando = ref(false);
const novoItemForm = ref(criarItemPadrao());

const novoItemErros = ref({
  tipoDeEquipamento: "",
  tipoDeServico: "",
  ncm: "",
  descricao: "",
  quantidade: "",
  kv: "",
  percentuais: ""
});

// Item de equipamento tipo SERVICOS: o tipo de venda do orcamento nao interfere no calculo fiscal
const itemEmEdicaoEhServico = computed(() => itemEhServico(novoItemForm.value));
// BUGFIX (a pedido do usuário): sem nenhum Tipo de Equipamento selecionado, tipoDeEquipamentoDescricao
// fica vazio — o que NÃO é "SERVICOS", então itemEmEdicaoEhServico dava false e os campos fiscais de
// Produto (% IPI, % Base de Cálculo do ICMS, % ICMS) ficavam habilitados mesmo sem tipo escolhido.
// Agora exige um Tipo de Equipamento selecionado (de Produto) para habilitar esses campos.
const temTipoDeEquipamentoSelecionado = computed(() => Boolean(novoItemForm.value.tipoDeEquipamento));
// BUGFIX (a pedido do usuário): campos de imposto de Produto (%IPI, %Base ICMS, %ICMS) estavam
// liberados assim que o Tipo de Equipamento era escolhido, sem exigir o NCM — permitindo digitar
// IPI/ICMS antes do NCM selecionado. Mesma trava de camposBasicosItemDesabilitados: para Produto,
// exige Tipo de Equipamento E NCM.
const campoProdutoDesabilitado = computed(
  () => !temTipoDeEquipamentoSelecionado.value || !novoItemForm.value.ncm || itemEmEdicaoEhServico.value
);
// Os campos fiscais de Serviço (% Base de Cálculo e % de cada imposto) só habilitam depois que,
// além de o item ser SERVICOS, o usuário também já selecionou o Tipo de Serviço — não basta
// trocar o Tipo de Equipamento para SERVICOS.
const campoServicoDesabilitado = computed(
  () => !itemEmEdicaoEhServico.value || !novoItemForm.value.tipoDeServico
);
// NCM só se aplica a item de Produto — e só pode ser selecionado depois que um Tipo de Equipamento
// (de Produto) já foi escolhido, mesma lógica dos demais campos fiscais de Produto.
const campoNcmDesabilitado = computed(
  () => !temTipoDeEquipamentoSelecionado.value || itemEmEdicaoEhServico.value
);

// Enquanto o Tipo de Equipamento nao for SERVICOS, o Tipo de Servico permanece desabilitado (mesma regra do JOB)
const desativaTipoDeServicoItem = computed(() => !itemEmEdicaoEhServico.value);

// Qtd / Custo Disp. / Preço de Venda / KI (a pedido do usuário): mesma trava dos demais campos
// fiscais — para item de Produto, exige Tipo de Equipamento E NCM já selecionados; para item de
// Serviço, exige o Tipo de Serviço já selecionado. Só depois disso o usuário pode preencher os
// dados comerciais do item.
const camposBasicosItemDesabilitados = computed(() => {
  if (itemEmEdicaoEhServico.value) {
    return !novoItemForm.value.tipoDeServico;
  }
  return !temTipoDeEquipamentoSelecionado.value || !novoItemForm.value.ncm;
});

// Recap isenta PIS/COFINS apenas para itens de produto (servico tem cadeia propria)
const pisCofinsIsentosPorRecap = computed(() => (
  !itemEmEdicaoEhServico.value && props.formulario?.tipoOrcamentoVenda === "Recap"
));

const novoItemCalculado = computed(() => {
  const comerciais = recalcularCamposComerciaisItem(novoItemForm.value);
  const itemAtualizado = { ...novoItemForm.value, ...comerciais };
  const impostosVenda = calcularImpostosVendaItem(itemAtualizado, props.formulario?.tipoOrcamentoVenda);
  return { ...comerciais, ...impostosVenda };
});

// BUGFIX (achado #3 da Rodada 4): novoItemErros.kv só é (re)calculado dentro de confirmarNovoItem(),
// ou seja, só no clique em "Adicionar". Como novoItemCalculado.kv é reativo (recalcula a cada
// alteração do formulário), era possível corrigir o KI/preço, o KV virar > 0 na tela, e a mensagem
// "KV deve ser maior que zero" continuar presa até um novo clique em Adicionar. Este computed reavalia
// o KV atual a cada render e só mantém a mensagem enquanto o valor realmente continuar inválido.
const novoItemErroKvExibido = computed(() => {
  const kvAtual = parseNumeroLocal(novoItemCalculado.value.kv);
  return kvAtual > 0 ? "" : novoItemErros.value.kv;
});

const novoItemReadonly = computed(() => ({
  kidoCusto: formatarNumeroInput(novoItemCalculado.value.kidoCusto),
  precoVenda_cIPI_cICMS: formatarNumeroInput(novoItemCalculado.value.precoVenda_cIPI_cICMS),
  precoVenda_sIPI_sICMS: formatarNumeroInput(novoItemCalculado.value.precoVenda_sIPI_sICMS),
  precoVenda_cISS: formatarNumeroInput(novoItemCalculado.value.precoVenda_cISS),
  kv: formatarNumeroInput(novoItemCalculado.value.kv, 3),
  valorICMS: formatarNumeroInput(novoItemCalculado.value.valorICMS),
  valorIPI: formatarNumeroInput(novoItemCalculado.value.valorIPI),
  valorDisponivelTotal: formatarNumeroInput(novoItemCalculado.value.valorDisponivelTotal),
  vlrTotal: formatarNumeroInput(novoItemCalculado.value.vlrTotal),
  precoTotalLiquido: formatarNumeroInput(novoItemCalculado.value.precoTotalLiquido),
  baseCalculoICMS: formatarNumeroInput(novoItemCalculado.value.baseCalculoICMS),
  baseCalculoPIS: formatarNumeroInput(novoItemCalculado.value.baseCalculoPIS),
  valorPIS: formatarNumeroInput(novoItemCalculado.value.valorPIS),
  baseCalculoCOFINS: formatarNumeroInput(novoItemCalculado.value.baseCalculoCOFINS),
  valorCOFINS: formatarNumeroInput(novoItemCalculado.value.valorCOFINS),
  baseCalculoISS: formatarNumeroInput(novoItemCalculado.value.baseCalculoISS),
  valorISS: formatarNumeroInput(novoItemCalculado.value.valorISS),
  baseCalculoIRRF: formatarNumeroInput(novoItemCalculado.value.baseCalculoIRRF),
  valorIRRF: formatarNumeroInput(novoItemCalculado.value.valorIRRF),
  baseCalculoCSLL: formatarNumeroInput(novoItemCalculado.value.baseCalculoCSLL),
  valorCSLL: formatarNumeroInput(novoItemCalculado.value.valorCSLL),
  baseCalculoINSS: formatarNumeroInput(novoItemCalculado.value.baseCalculoINSS),
  valorINSS: formatarNumeroInput(novoItemCalculado.value.valorINSS)
}));

const modoVisualizacaoItem = ref(false);

const itemSomenteLeitura = computed(() => modoVisualizacaoItem.value);

const tituloDialogItem = computed(() => (
  modoVisualizacaoItem.value
    ? "Visualizar Item da Solicitação"
    : (indiceItemEmEdicao.value !== null ? "Editar Item da Solicitação" : "Novo Item da Solicitação")
));

function criarNovoItemVazio() {
  return criarItemPadrao();
}

function extrairListaResposta(response) {
  const candidatos = [
    response?.data?.data?.data,
    response?.data?.data,
    response?.data,
    response
  ];
  return candidatos.find((valor) => Array.isArray(valor)) || [];
}

function normalizarTipoItemOpcao(item = {}) {
  const origem = item?._currentData || item?.currentData || item;
  return {
    ...item,
    id: origem?.id || origem?._id || item?.id || item?._id || "",
    descricao: origem?.descricao || origem?.description || origem?.nome || origem?.name || "",
    sigla: origem?.sigla || origem?.codigo || origem?.code || ""
  };
}

function normalizarTipoServicoOpcao(item = {}) {
  const origem = item?._currentData || item?.currentData || item;
  const servicoCodigo = origem?.servicoCodigo || origem?.codigo || origem?.code || "";
  const servicoDescricao = origem?.servicoDescricao || origem?.descricao || origem?.description || origem?.nome || origem?.name || "";
  return {
    ...item,
    id: origem?.id || origem?._id || item?.id || item?._id || "",
    servicoCodigo,
    servicoDescricao,
    // Usado como item-text do v-autocomplete: no Vuetify 2, em modo single-select
    // o texto exibido no campo fechado vem de item-text (não do slot "selection"),
    // por isso precisa de uma string única pronta, e não só dos campos separados.
    servicoLabel: [servicoCodigo, servicoDescricao].filter(Boolean).join(" - ")
  };
}

function normalizarNcmOpcao(item = {}) {
  const origem = item?._currentData || item?.currentData || item;
  const descricaoCompleta =
    origem?.descricaoCompleta || origem?.descricao || origem?.description || origem?.nome || origem?.name || "";
  return {
    ...item,
    id: origem?.id || origem?._id || item?.id || item?._id || "",
    descricaoCompleta,
    codigo: origem?.codigo || origem?.code || ""
  };
}

async function carregarNcmItem() {
  if (itensNcm.value.length > 0 || itensNcmCarregando.value) return;
  itensNcmCarregando.value = true;
  try {
    // Mesma fonte usada pelo item do JOB (ver ModalRevisarDados.vue / ModalPreencherDadosComercial.vue):
    // View "job-ncms" (catálogo completo carregado de uma vez, sem paginação/busca server-side).
    const response = await ERP.getManyWithView(View.JOB_NCMS);
    itensNcm.value = extrairListaResposta(response)
      .map(normalizarNcmOpcao)
      .filter((item) => Boolean(item.id));
  } catch (_) {
    itensNcm.value = [];
  } finally {
    itensNcmCarregando.value = false;
  }
}

// BUGFIX (a pedido do usuário): "no visualizar depois de salvo o bloco de totais de serviço não
// carregou e os itens não vieram... serviço não veio com o tipo de serviço". Causa raiz: o backend
// devolve tipoDeEquipamento/tipoDeServico/ncm do item salvo apenas como o ID puro (sem objeto
// expandido) — os campos-espelho *Descricao (tipoDeEquipamentoDescricao, tipoDeServicoDescricao,
// ncmDescricao) chegam vazios do reload, mesmo com o ID certo salvo (confirmado: tipoDeServico tinha
// o id correto, só a descrição é que sumia). Isso é grave porque tanto "hasItensServico" (decide se
// mostra o bloco "Impostos de Serviço" no resumo) quanto o dialog de item (decide se mostra "Tipo de
// Serviço" ou "NCM") dependem só de tipoDeEquipamentoDescricao === "SERVICOS" — nunca do ID. Resolve
// aqui, por ID, contra os catálogos já carregados (mesmos usados pelos dropdowns), e MUTA o item em
// vigor (não substitui a referência) pra manter a reatividade do Vue 2 — a chave já existe no objeto.
function backfillDescricaoItem(item) {
  if (!item) return;
  if (!item.tipoDeEquipamentoDescricao && item.tipoDeEquipamento) {
    const achado = itensTiposDeEquipamento.value.find((opcao) => opcao?.id === item.tipoDeEquipamento);
    if (achado?.descricao) item.tipoDeEquipamentoDescricao = achado.descricao;
  }
  if (!item.tipoDeServicoDescricao && item.tipoDeServico) {
    const achado = itensTiposDeServico.value.find((opcao) => opcao?.id === item.tipoDeServico);
    if (achado?.servicoDescricao) item.tipoDeServicoDescricao = achado.servicoDescricao;
  }
  if (!item.ncmDescricao && item.ncm) {
    const achado = itensNcm.value.find((opcao) => opcao?.id === item.ncm);
    if (achado?.descricaoCompleta) item.ncmDescricao = achado.descricaoCompleta;
  }
}

// Roda o backfill acima em todos os itens já carregados na Solicitação (usado ao abrir o formulário,
// pra corrigir o bloco "Impostos de Serviço" do resumo, que lê direto de props.formulario.itens).
async function backfillDescricoesItens() {
  await Promise.all([carregarTiposDeEquipamentoServicoItem(), carregarNcmItem()]);
  (props.formulario?.itens || []).forEach(backfillDescricaoItem);
}

function aoSelecionarNcmItem(id) {
  const selecionado = itensNcm.value.find((item) => item?.id === id);
  novoItemForm.value.ncmDescricao = selecionado?.descricaoCompleta || "";
}

async function carregarTiposDeEquipamentoServicoItem() {
  if (itensTiposDeEquipamento.value.length === 0 && !itensTiposDeEquipamentoCarregando.value) {
    itensTiposDeEquipamentoCarregando.value = true;
    try {
      // Mesma fonte usada pelo item do JOB (ver ModalRevisarDados.vue / ModalPreencherDadosComercial.vue):
      // View "job-item-tipos-de-equipamento" (catálogo curado de ~20 categorias, incluindo "SERVICOS"),
      // sem fallback para o cadastro completo de TipoDeEquipamento — esse cadastro é um catálogo maior e
      // diferente (siglas D/P, usado no campo do cabeçalho), que não contém a categoria "SERVICOS" e por
      // isso nunca habilitava o campo "Tipo de Serviço" quando a lista caía nesse fallback.
      const response = await ERP.getManyWithView(View.JOB_ITEM_TIPOS_DE_EQUIPAMENTO);
      itensTiposDeEquipamento.value = extrairListaResposta(response)
        .map(normalizarTipoItemOpcao)
        .filter((item) => Boolean(item.id));
    } catch (_) {
      itensTiposDeEquipamento.value = [];
    } finally {
      itensTiposDeEquipamentoCarregando.value = false;
    }
  }

  if (itensTiposDeServico.value.length === 0 && !itensTiposDeServicoCarregando.value) {
    itensTiposDeServicoCarregando.value = true;
    try {
      const response = await ERP.getManyWithView(View.JOB_ITEM_TIPOS_DE_SERVICO);
      const lista = extrairListaResposta(response)
        .map(normalizarTipoServicoOpcao)
        .filter((item) => Boolean(item.id));
      if (lista.length > 0) {
        itensTiposDeServico.value = lista;
      } else {
        const fallback = await new EntityService("TipoDeServico").getAll(1, 500, "", "", null, null, null, "descricao");
        itensTiposDeServico.value = extrairListaResposta(fallback)
          .map(normalizarTipoServicoOpcao)
          .filter((item) => Boolean(item.id));
      }
    } catch (_) {
      try {
        const response = await new EntityService("TipoDeServico").getAll(1, 500, "", "", null, null, null, "descricao");
        itensTiposDeServico.value = extrairListaResposta(response)
          .map(normalizarTipoServicoOpcao)
          .filter((item) => Boolean(item.id));
      } catch (_) {
        itensTiposDeServico.value = [];
      }
    } finally {
      itensTiposDeServicoCarregando.value = false;
    }
  }
}

const itensTiposDeEquipamentoFiltrados = computed(() => itensTiposDeEquipamento.value);

function aoSelecionarTipoDeEquipamentoItem(id) {
  const selecionado = itensTiposDeEquipamento.value.find((item) => item?.id === id);
  novoItemForm.value.tipoDeEquipamentoDescricao = selecionado?.descricao || "";

  const camposFiscais = [
    "porcentagemBaseCalculoICMS",
    "baseCalculoICMS",
    "porcentagemICMSVenda",
    "porcentagemBaseCalculoISS",
    "baseCalculoISS",
    "porcentagemISS",
    "porcentagemBaseCalculoIRRF",
    "baseCalculoIRRF",
    "porcentagemIRRF",
    "porcentagemBaseCalculoPIS",
    "baseCalculoPIS",
    "porcentagemPIS",
    "valorPIS",
    "porcentagemBaseCalculoCOFINS",
    "baseCalculoCOFINS",
    "porcentagemCOFINS",
    "valorCOFINS",
    "porcentagemBaseCalculoCSLL",
    "baseCalculoCSLL",
    "porcentagemCSLL",
    "valorCSLL",
    "porcentagemBaseCalculoINSS",
    "baseCalculoINSS",
    "porcentagemINSS",
    "valorINSS",
    "valorICMS",
    "valorIPI",
    "precoTotalLiquido"
  ];

  camposFiscais.forEach((campo) => {
    novoItemForm.value[campo] = "0,00";
  });

  // % Base de Cálculo do ICMS (Produto) já habilita neste momento, então já começa em 100%.
  // Os de Serviço (PIS/COFINS/ISS/IRRF/CSLL/INSS) continuam em 0% aqui: só ficam habilitados
  // e pulam para 100% quando o usuário também selecionar o Tipo de Serviço, em
  // aoSelecionarTipoDeServicoItem() logo abaixo.
  const ehServicoAgora = itemEhServico(novoItemForm.value);
  if (!ehServicoAgora) {
    novoItemForm.value.porcentagemBaseCalculoICMS = "100,00";
    // BUGFIX (a pedido do usuário): calcularImpostosVendaProduto (rules.js) já calcula a Base de
    // Cálculo do PIS/COFINS de item Produto usando 100% do Preço Total Líquido — o cálculo sempre
    // esteve certo — mas o campo "% Base de Cálculo" continuava exibido em 0,00, dando a impressão
    // de que a base estava zerada. Reflete aqui o mesmo 100% assumido no cálculo, exceto no Recap,
    // que isenta PIS/COFINS de produto (base e valor ficam 0 de propósito).
    if (props.formulario?.tipoOrcamentoVenda !== "Recap") {
      novoItemForm.value.porcentagemBaseCalculoPIS = "100,00";
      novoItemForm.value.porcentagemBaseCalculoCOFINS = "100,00";
    }
  }

  // Se o item deixou de ser SERVICOS, limpa o Tipo de Servico e os campos ligados a ele
  if (!ehServicoAgora) {
    novoItemForm.value.tipoDeServico = "";
    novoItemForm.value.tipoDeServicoDescricao = "";

    if (id && ["Consumo", "Revenda"].includes(props.formulario?.tipoOrcamentoVenda)) {
      novoItemForm.value.porcentagemPIS = "1,65";
      novoItemForm.value.porcentagemCOFINS = "7,60";
    }
  } else {
    // Item virou SERVICOS: NCM não se aplica a serviço, limpa pra não ficar um valor de produto
    // "grudado" (e pra não disparar a validação de NCM obrigatório, que só vale pra Produto).
    novoItemForm.value.ncm = "";
    novoItemForm.value.ncmDescricao = "";
  }
}

function aoSelecionarTipoDeServicoItem(id) {
  const selecionado = itensTiposDeServico.value.find((item) => item?.id === id);
  if (!selecionado) return;

  novoItemForm.value.tipoDeServicoDescricao = selecionado?.servicoDescricao || selecionado?.descricao || "";

  // Pre-carrega aliquotas do Tipo de Servico para auxiliar o preenchimento, sem sobrescrever valores ja digitados
  const preCarregar = (campoDestino, valorOrigem) => {
    const atual = parseNumeroLocal(novoItemForm.value[campoDestino]);
    if (atual > 0) return;
    if (valorOrigem === undefined || valorOrigem === null || valorOrigem === "") return;
    novoItemForm.value[campoDestino] = formatarNumeroInput(valorOrigem);
  };

  preCarregar("porcentagemBaseCalculoISS", selecionado?.porcentagemBaseCalculoISS);
  preCarregar("porcentagemISS", selecionado?.porcentagemISS);
  preCarregar("porcentagemBaseCalculoIRRF", selecionado?.porcentagemBaseCalculoIRRF);
  preCarregar("porcentagemIRRF", selecionado?.porcentagemIRRF);
  preCarregar("porcentagemBaseCalculoPIS", selecionado?.porcentagemBaseCalculoPIS);
  preCarregar("porcentagemPIS", selecionado?.porcentagemPIS);
  preCarregar("porcentagemBaseCalculoCOFINS", selecionado?.porcentagemBaseCalculoCOFINS);
  preCarregar("porcentagemCOFINS", selecionado?.porcentagemCOFINS);
  preCarregar("porcentagemBaseCalculoCSLL", selecionado?.porcentagemBaseCalculoCSLL);
  preCarregar("porcentagemCSLL", selecionado?.porcentagemCSLL);
  preCarregar("porcentagemBaseCalculoINSS", selecionado?.porcentagemBaseCalculoINSS);
  preCarregar("porcentagemINSS", selecionado?.porcentagemINSS);

  // Os campos "% Base de Cálculo" de Serviço só habilitam agora que o Tipo de Serviço foi
  // selecionado (campoServicoDesabilitado). Aqui já começam em 100% — exceto o(s) que o
  // preCarregar acima já tiver preenchido com uma alíquota vinda do cadastro do Tipo de Serviço.
  const camposBaseCalculoServico = [
    "porcentagemBaseCalculoISS",
    "porcentagemBaseCalculoIRRF",
    "porcentagemBaseCalculoPIS",
    "porcentagemBaseCalculoCOFINS",
    "porcentagemBaseCalculoCSLL",
    "porcentagemBaseCalculoINSS"
  ];
  camposBaseCalculoServico.forEach((campo) => {
    if (parseNumeroLocal(novoItemForm.value[campo]) <= 0) {
      novoItemForm.value[campo] = "100,00";
    }
  });
}

function montarDescricaoItemGrid(item = {}) {
  return String(item?.descricao || item?.description || "").trim() || "-";
}

function obterTipoEquipamentoPorId(id) {
  const chave = String(id || "").trim();
  if (!chave) return null;

  const fontes = [
    ...(Array.isArray(itensTiposDeEquipamento.value) ? itensTiposDeEquipamento.value : []),
    ...(Array.isArray(props.tiposDeEquipamento) ? props.tiposDeEquipamento : [])
  ];

  return fontes.find((tipo) => String(tipo?.id || "").trim() === chave) || null;
}

function montarTipoEquipamentoItemGrid(item = {}) {
  const mirror = item?._mirror_tipoDeEquipamento;
  const mirrorObj = mirror && typeof mirror === "object" ? mirror : null;
  const tipoEquipamentoId = String(item?.tipoDeEquipamento || (typeof mirror === "string" ? mirror : "")).trim();
  const tipoEquipamentoCatalogo = obterTipoEquipamentoPorId(tipoEquipamentoId);

  const sigla = String(
    item?.tipoDeEquipamentoSigla ||
    mirrorObj?.sigla ||
    mirrorObj?.codigo ||
    tipoEquipamentoCatalogo?.sigla ||
    tipoEquipamentoCatalogo?.codigo ||
    item?.sigla ||
    ""
  ).trim();

  const descricao = String(
    item?.tipoDeEquipamentoDescricao ||
    mirrorObj?.descricao ||
    mirrorObj?.nome ||
    tipoEquipamentoCatalogo?.descricao ||
    tipoEquipamentoCatalogo?.nome ||
    ""
  ).trim();

  if (!sigla && !descricao) return "-";
  if (!sigla) return descricao;
  if (!descricao || descricao === sigla) return sigla;
  return `${sigla} - ${descricao}`;
}

function recalcularNovoItemForm() {}

// Recap zera e bloqueia PIS/COFINS na tela para itens de produto (servico segue cadeia propria)
watch(pisCofinsIsentosPorRecap, (isento) => {
  if (!isento) return;
  novoItemForm.value.porcentagemPIS = "0,00";
  novoItemForm.value.porcentagemCOFINS = "0,00";
});

watch(
  () => [
    novoItemForm.value.quantidade,
    novoItemForm.value.valorDisponivel,
    novoItemForm.value.ki,
    novoItemForm.value.porcentagemISS,
    novoItemForm.value.porcentagemICMSVenda,
    novoItemForm.value.pocentagemIPIVenda,
    novoItemForm.value.precoVenda_sIPI_cICMS
  ],
  () => {
    if (!dialogNovoItem.value) return;
    recalcularNovoItemForm();
  }
);

function normalizarTexto(valor = "") {
  return String(valor || "")
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase();
}

const statusOpcoesFollowUp = computed(() => {
  return (Array.isArray(props.statusOpcoes) ? props.statusOpcoes : []).filter((item) => {
    const descricao = normalizarTexto(item?.description || item?.descricao || item?.name);
    return descricao !== "PEDIDO COLOCADO" && descricao !== "CONCLUIDO";
  });
});

const statusConcluidoFollowUp = computed(() => {
  return (Array.isArray(props.statusOpcoes) ? props.statusOpcoes : []).find((item) => {
    const descricao = normalizarTexto(item?.description || item?.descricao || item?.name);
    return descricao === "CONCLUIDO";
  }) || null;
});

const descricaoStatusConcluidoFollowUp = computed(() => {
  return statusConcluidoFollowUp.value?.description || "CONCLUÍDO";
});

function formatarData(valor) {
  if (!valor) return "-";
  const dataBrasil = UtilDateTime.toFormatBrazil({ dateTime: valor });
  return dataBrasil || String(valor).trim() || "-";
}

const statusFormularioCancelado = computed(() => {
  const textoStatus = normalizarTexto(
    props.formulario?.statusDescription || props.formulario?.status || ""
  );
  return textoStatus.includes("CANCEL");
});

const deveConfirmarFechamentoCancelado = computed(() => {
  return props.modoFormulario === "edit" && statusFormularioCancelado.value;
});

const statusFormularioConcluido = computed(() => {
  if (props.statusConcluido) return true;
  const textoStatus = normalizarTexto(
    props.formulario?.statusDescription || props.formulario?.status || ""
  );
  return textoStatus === "CONCLUIDO";
});

const statusFormularioPedidoColocado = computed(() => {
  const textoStatus = normalizarTexto(
    props.formulario?.statusDescription || props.formulario?.status || ""
  );
  return textoStatus.includes("PEDIDO COLOCADO");
});

// MELHORIA: "bloqueiaAcoesEdicao" trava TUDO (cabeçalho, itens, follow-up) assim que o status
// vira CANCELADO — inclusive a inclusão de um NOVO Follow-Up. Isso deixava o usuário sem saída:
// ao incluir um Follow-Up com status CANCELADO por engano (ou querer reverter antes de fechar),
// o botão "+" de Follow-Up desaparecia imediatamente, sem chance de incluir outro Follow-Up com
// status editável para tirar a proposta do CANCELADO. O bloqueio definitivo de CANCELADO já
// acontece no fechamento (ver deveConfirmarFechamentoCancelado / diálogo "não poderá mais ser
// editada"), então aqui a inclusão de Follow-Up continua liberada mesmo com status CANCELADO —
// só PEDIDO COLOCADO (regra de negócio à parte, sem essa janela de correção) continua travando.
const podeIncluirFollowUp = computed(() => {
  if (!props.formulario?.id) return false;
  if (props.somenteLeitura) return false;
  return !statusFormularioPedidoColocado.value;
});

const podeEditarItens = computed(() => {
  if (formularioBloqueado.value) return false;

  const textoStatus = normalizarTexto(
    props.formulario?.statusDescription || props.formulario?.status || ""
  );

  if (!textoStatus) return true;
  if (textoStatus.includes("PEDIDO COLOCADO")) return false;
  if (textoStatus.includes("CONCLUIDO")) return true;

  return (
    textoStatus.includes("EM EXECUCAO") ||
    textoStatus.includes("NOVO") ||
    textoStatus.includes("REVISAO GERADA") ||
    textoStatus.includes("REVISAO")
  );
});

const podeExcluirItens = computed(() => {
  if (!podeEditarItens.value) return false;
  return !statusFormularioConcluido.value && !statusFormularioPedidoColocado.value;
});

// O botao de incluir item so fica habilitado apos selecionar o Tipo de Venda do orcamento
const podeAdicionarItem = computed(() => podeEditarItens.value && !!props.formulario?.tipoOrcamentoVenda);

function aoClicarFechar() {
  if (deveConfirmarFechamentoCancelado.value) {
    dialogConfirmarFechamentoCancelado.value = true;
    return;
  }

  emits("fechar");
}

function confirmarFechamentoCancelado() {
  dialogConfirmarFechamentoCancelado.value = false;
  emits("fechar");
}

function cancelarFechamentoCancelado() {
  dialogConfirmarFechamentoCancelado.value = false;
}

function abrirConfirmacaoConclusao() {
  dialogConfirmarConclusao.value = true;
}

function aoClicarConcluirSolicitacao() {
  if (!props.podeConcluirSolicitacao) {
    emits("concluir-solicitacao");
    return;
  }

  abrirConfirmacaoConclusao();
}

function cancelarConfirmacaoConclusao() {
  dialogConfirmarConclusao.value = false;
}

function confirmarConclusaoSolicitacao() {
  dialogConfirmarConclusao.value = false;
  emits("concluir-solicitacao");
}

function abrirSelecaoTipoRevisao() {
  tipoRevisaoSelecionado.value = null;
  dialogSelecionarTipoRevisao.value = true;
}

function cancelarSelecaoTipoRevisao() {
  tipoRevisaoSelecionado.value = null;
  dialogSelecionarTipoRevisao.value = false;
}

function confirmarSelecaoTipoRevisao() {
  if (!tipoRevisaoSelecionado.value) return;
  const tipoRevisao = tipoRevisaoSelecionado.value;
  tipoRevisaoSelecionado.value = null;
  dialogSelecionarTipoRevisao.value = false;
  emits("gerar-revisao", tipoRevisao);
}

function abrirNovoFollowUp() {
  followUpForm.value = {
    description: "",
    data: UtilDateTime.todayISO(),
    contato_comercial: "",
    status: statusFormularioConcluido.value ? (statusConcluidoFollowUp.value?.id || null) : null
  };
  followUpErros.value = { description: "", status: "", contato_comercial: "" };
  dialogFollowUp.value = true;
}

function confirmarFollowUp() {
  const descricao = String(followUpForm.value.description || "").trim();
  const status = statusFormularioConcluido.value
    ? (statusConcluidoFollowUp.value?.id || followUpForm.value.status)
    : followUpForm.value.status;
  const contatoComercial = String(followUpForm.value.contato_comercial || "").trim();

  if (statusFormularioConcluido.value && !statusConcluidoFollowUp.value?.id) {
    followUpErros.value = {
      description: descricao ? "" : "Descrição é obrigatória",
      status: "Status CONCLUÍDO não encontrado nas opções",
      contato_comercial: ""
    };
    return;
  }

  const statusSelecionado = statusOpcoesFollowUp.value.find((item) => item?.id === status);
  const descricaoStatus = normalizarTexto(
    statusSelecionado?.description ||
    statusSelecionado?.descricao ||
    statusSelecionado?.name ||
    descricaoStatusConcluidoFollowUp.value
  );
  const contatoObrigatorio = descricaoStatus === "CONCLUIDO";

  followUpErros.value = {
    description: descricao ? "" : "Descrição é obrigatória",
    status: status ? "" : "Status é obrigatório",
    contato_comercial: (contatoObrigatorio && !contatoComercial)
      ? "Contato comercial é obrigatório para status CONCLUÍDO"
      : ""
  };

  if (!descricao || !status || (contatoObrigatorio && !contatoComercial)) return;

  followUpForm.value.description = descricao;
  followUpForm.value.contato_comercial = contatoComercial;
  emits("novo-followup", { ...followUpForm.value });
  dialogFollowUp.value = false;
}

const headersFollowUp = [
  { text: "Código", value: "code", sortable: false },
  { text: "Descrição", value: "description", sortable: false },
  { text: "Data", value: "data", sortable: false },
  { text: "Contato Comercial", value: "contato_comercial", sortable: false },
  { text: "Status", value: "statusDescription", sortable: false }
];

const headersRevisoes = [
  { text: "Revisão Comercial", value: "version", sortable: false },
  { text: "Revisão Técnica", value: "versionTec", sortable: false },
  { text: "Tipo", value: "tipoVersion", sortable: false },
  { text: "Número Proposta", value: "numeroProposta", sortable: false },
  { text: "Data Geração", value: "dateGenerate", sortable: false },
  { text: "Data Desejada Comercial", value: "dataDesejadaComercial", sortable: false },
  { text: "Data Desejada Técnica", value: "dataDesejada", sortable: false },
  { text: "Status", value: "statusDescription", sortable: false }
];

const headersItens = [
  { text: "Ações", value: "acoes", sortable: false, width: "90px" },
  { text: "Descrição", value: "descricao", sortable: false },
  { text: "Tipo de Equipamento", value: "tipoDeEquipamento", sortable: false, width: "200px" },
  { text: "Qtd", value: "quantidade", sortable: false, width: "80px" },
  { text: "Custo Unit.", value: "valorDisponivel", sortable: false, width: "120px" },
  { text: "Custo Total", value: "valorDisponivelTotal", sortable: false, width: "120px" },
  { text: "Preço de Venda Unitário", value: "precoVenda_sIPI_cICMS", sortable: false, width: "150px" },
  { text: "Preço de Venda Total", value: "valorTotal", sortable: false, width: "135px" },
  { text: "%IPI", value: "pocentagemIPIVenda", sortable: false, width: "105px" },
  { text: "%ICMS", value: "porcentagemICMSVenda", sortable: false, width: "115px" },
  { text: "%PIS", value: "porcentagemPIS", sortable: false, width: "85px" },
  { text: "%COFINS", value: "porcentagemCOFINS", sortable: false, width: "95px" },
  { text: "%ISS", value: "porcentagemISS", sortable: false, width: "100px" },
  { text: "KI", value: "ki", sortable: false, width: "90px" },
  { text: "KV", value: "kv", sortable: false, width: "90px" }
];

const historicoDialog = ref(false);
const historicoLoading = ref(false);
const historicoItems = ref([]);
const historicoItemSelected = ref(null);

function openHistorico(item, itemIndex) {
  historicoItemSelected.value = { ...item };
  historicoDialog.value = true;
  historicoLoading.value = true;
  historicoItems.value = [];

  // TODO: Implementar carregamento de histórico do item
  // Padrão: utilizar entityServiceHistorico.getAll(1, 1000, item.id, ['solicitacaoDeOrcamentoItem'], ...)
  // Por enquanto retorna um mock ou vazio para estrutura estar pronta
  
  // Simulação: em produção, fazer chamada ao backend
  setTimeout(() => {
    historicoLoading.value = false;
    // historicoItems.value será populado pela chamada ao backend
  }, 500);
}

async function abrirNovoItem() {
  if (!podeAdicionarItem.value) return;
  if (dialogNovoItem.value) return;

  modoVisualizacaoItem.value = false;
  indiceItemEmEdicao.value = null;
  novoItemForm.value = criarNovoItemVazio();
  novoItemErros.value = { tipoDeEquipamento: "", tipoDeServico: "", ncm: "", descricao: "", quantidade: "", kv: "", percentuais: "" };

  carregarTiposDeEquipamentoServicoItem();
  carregarNcmItem();
  recalcularNovoItemForm();
  dialogNovoItem.value = true;
}

async function abrirEditarItem(item = {}, index = -1) {
  if (!podeEditarItens.value) return;
  if (!Number.isInteger(index) || index < 0) return;

  modoVisualizacaoItem.value = false;
  indiceItemEmEdicao.value = index;

  // BUGFIX (ver comentário em backfillDescricaoItem): espera os catálogos carregarem e preenche
  // tipoDeEquipamentoDescricao/tipoDeServicoDescricao/ncmDescricao ANTES de copiar o item pro form —
  // senão o dialog decide "Tipo de Serviço" x "NCM" com base num campo ainda vazio.
  await carregarTiposDeEquipamentoServicoItem();
  await carregarNcmItem();
  backfillDescricaoItem(item);

  novoItemForm.value = {
    ...criarNovoItemVazio(),
    ...item,
    descricao: item?.descricao || item?.description || ""
  };
  novoItemErros.value = { tipoDeEquipamento: "", tipoDeServico: "", ncm: "", descricao: "", quantidade: "", kv: "", percentuais: "" };

  recalcularNovoItemForm();
  dialogNovoItem.value = true;
}

async function abrirVisualizarItem(item = {}, index = -1) {
  if (!Number.isInteger(index) || index < 0) return;

  modoVisualizacaoItem.value = true;
  indiceItemEmEdicao.value = index;

  // BUGFIX: mesmo backfill do abrirEditarItem (ver comentário em backfillDescricaoItem) — sem isso o
  // "Visualizar" mostrava o campo "NCM" vazio no lugar de "Tipo de Serviço" num item de Serviço.
  await carregarTiposDeEquipamentoServicoItem();
  await carregarNcmItem();
  backfillDescricaoItem(item);

  novoItemForm.value = {
    ...criarNovoItemVazio(),
    ...item,
    descricao: item?.descricao || item?.description || ""
  };
  novoItemErros.value = { tipoDeEquipamento: "", tipoDeServico: "", ncm: "", descricao: "", quantidade: "", kv: "", percentuais: "" };

  recalcularNovoItemForm();
  dialogNovoItem.value = true;
}

watch(
  () => props.formularioAberto,
  (aberto) => {
    if (!aberto) return;
    // BUGFIX (a pedido do usuário): dispara o backfill de tipoDeEquipamentoDescricao/
    // tipoDeServicoDescricao/ncmDescricao pra TODOS os itens já carregados assim que o formulário abre
    // (Editar/Visualizar) — é o que corrige o bloco "Impostos de Serviço" não aparecer no resumo, já
    // que "hasItensServico" lê direto de props.formulario.itens, não do dialog de item individual.
    backfillDescricoesItens();
  },
  { immediate: true }
);

function confirmarNovoItem() {
  const tipoDeEquipamento = String(novoItemForm.value.tipoDeEquipamento || "").trim();
  const tipoDeServico = String(novoItemForm.value.tipoDeServico || "").trim();
  const ncm = String(novoItemForm.value.ncm || "").trim();
  const descricao = String(novoItemForm.value.descricao || "").trim();
  const quantidade = parseNumeroLocal(novoItemForm.value.quantidade);
  const ehServico = itemEhServico(novoItemForm.value);
  const calculado = novoItemCalculado.value;
  const kvCalculado = parseNumeroLocal(calculado.kv);

  // Todo (Regras de Preenchimento de Campos de Impostos): só valida os percentuais relevantes pro
  // tipo do item (produto x serviço) — os do outro tipo ficam desabilitados/zerados na tela.
  const camposPercentualRelevantes = ehServico ? CAMPOS_PERCENTUAL_SERVICO : CAMPOS_PERCENTUAL_PRODUTO;
  const percentualInvalido = camposPercentualRelevantes.some(
    (campo) => percentualForaDoIntervalo(novoItemForm.value[campo])
  );

  // NCM só existe/faz sentido para item de Produto (Serviço usa NBS na nota, não NCM) — obrigatório
  // com uma opção selecionada apenas quando o item não é Serviço.
  const ncmInvalido = !ehServico && !ncm;

  novoItemErros.value = {
    tipoDeEquipamento: tipoDeEquipamento ? "" : "Tipo de Equipamento é obrigatório",
    tipoDeServico: (ehServico && !tipoDeServico) ? "Tipo de Serviço é obrigatório" : "",
    ncm: ncmInvalido ? "NCM é obrigatório" : "",
    descricao: descricao ? "" : "Descrição é obrigatória",
    quantidade: quantidade > 0 ? "" : "Quantidade deve ser maior que zero",
    kv: kvCalculado > 0 ? "" : "KV deve ser maior que zero",
    percentuais: percentualInvalido ? "Todos os campos de porcentagem devem estar entre 0% e 100%" : ""
  };
  if (
    !tipoDeEquipamento || (ehServico && !tipoDeServico) || ncmInvalido || !descricao ||
    quantidade <= 0 || kvCalculado <= 0 || percentualInvalido
  ) {
    return false;
  }

  try {
    const payloadItem = {
      ...novoItemForm.value,
      descricao,
      description: descricao,
      quantidade,
      qty: quantidade,
      valorDisponivelTotal: calculado.valorDisponivelTotal,
      kidoCusto: calculado.kidoCusto,
      kv: calculado.kv,
      precoVenda_cIPI_cICMS: calculado.precoVenda_cIPI_cICMS,
      precoVenda_sIPI_sICMS: calculado.precoVenda_sIPI_sICMS,
      precoVenda_cISS: calculado.precoVenda_cISS,
      valorICMS: calculado.valorICMS,
      valorIPI: calculado.valorIPI,
      vlrTotal: calculado.vlrTotal,
      valorTotal: calculado.vlrTotal,
      porcentagemPIS: calculado.porcentagemPIS ?? novoItemForm.value.porcentagemPIS,
      porcentagemCOFINS: calculado.porcentagemCOFINS ?? novoItemForm.value.porcentagemCOFINS,
      precoTotalLiquido: calculado.precoTotalLiquido,
      baseCalculoICMS: calculado.baseCalculoICMS,
      baseCalculoPIS: calculado.baseCalculoPIS,
      valorPIS: calculado.valorPIS,
      baseCalculoCOFINS: calculado.baseCalculoCOFINS,
      valorCOFINS: calculado.valorCOFINS,
      baseCalculoISS: calculado.baseCalculoISS,
      valorISS: calculado.valorISS,
      baseCalculoIRRF: calculado.baseCalculoIRRF,
      valorIRRF: calculado.valorIRRF,
      baseCalculoCSLL: calculado.baseCalculoCSLL,
      valorCSLL: calculado.valorCSLL,
      baseCalculoINSS: calculado.baseCalculoINSS,
      valorINSS: calculado.valorINSS,
      observacao: novoItemForm.value.observacao
    };

    if (indiceItemEmEdicao.value !== null) {
      emits("update-item", { index: indiceItemEmEdicao.value, item: payloadItem });
    } else {
      emits("add-item", payloadItem);
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Erro ao adicionar item da solicitação:", error);
    return false;
  }

  return true;
}

function cancelarNovoItem() {
  modoVisualizacaoItem.value = false;
  indiceItemEmEdicao.value = null;
  dialogNovoItem.value = false;
}

function adicionarNovoItem() {
  if (modoVisualizacaoItem.value) {
    cancelarNovoItem();
    return;
  }

  const sucesso = confirmarNovoItem();
  if (!sucesso) return;
  indiceItemEmEdicao.value = null;
  dialogNovoItem.value = false;
}
</script>

<template>
  <div>
    <v-dialog v-model="dialogModel" fullscreen persistent transition="dialog-bottom-transition">
      <v-card tile class="so-modal">
        <div class="so-topbar">
          <div class="so-topbar__left-actions">
            <v-btn class="so-topbar__btn so-topbar__btn--close" color="error" dark :disabled="props.carregando" @click="aoClicarFechar">
              Fechar
            </v-btn>
            <v-btn
              class="so-topbar__btn so-topbar__btn--proposal"
              color="#0D47A1"
              dark
              :disabled="props.carregando"
              @click="emits('gerar-capa-proposta')"
            >
              Gerar Capa Proposta
            </v-btn>
          </div>

          <p class="so-topbar__title">
            <v-icon color="white" class="mr-2">mdi-file-document-edit-outline</v-icon>
            Solicitação de Orçamento
          </p>

          <div class="so-topbar__actions">
            <v-btn
              v-if="podeEditarCabecalho && !statusFormularioConcluido"
              class="so-topbar__btn"
              color="#0D47A1"
              dark
              :loading="props.carregando"
              @click="emits('salvar')"
            >
              Gravar
            </v-btn>
            <v-btn
              v-if="podeEditarCabecalho && statusFormularioConcluido && !props.hasJobAssociado"
              class="so-topbar__btn"
              color="warning"
              dark
              :disabled="props.carregando || !props.formulario?.id"
              @click="abrirSelecaoTipoRevisao"
            >
              Gerar Revisão
            </v-btn>
            <v-btn
              v-if="podeEditarCabecalho && props.podeGerarJob"
              class="so-topbar__btn"
              color="success"
              dark
              :disabled="props.carregando || !props.formulario?.id"
              @click="emits('gerar-job')"
            >
                Gerar Job
            </v-btn>
            <v-btn
              v-if="podeEditarCabecalho && !statusFormularioConcluido"
              class="so-topbar__btn so-topbar__btn--auto-width"
              color="success"
              dark
              :disabled="props.carregando || !props.formulario?.id"
              @click="aoClicarConcluirSolicitacao()"
            >
              Concluir Solicitação
            </v-btn>
          </div>
        </div>

        <div class="so-body">
          <div :class="['so-form', { 'so-form--readonly': formularioBloqueado }]">
            <fieldset class="so-fieldset">

            <!-- Cabecalho -->
            <v-row dense align="center" class="mb-0">
              <v-col cols="12" md="3">
                <v-text-field
                  label="Número Proposta"
                  v-model="props.formulario.numeroProposta"
                  readonly disabled outlined dense hide-details class="so-readonly"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-autocomplete
                  :items="tiposDeEquipamentoFiltrados"
                  :item-text="tipoEquipamentoItemText"
                  :filter="tipoEquipamentoFilter"
                  item-value="id"
                  label="Tipo de Equipamento"
                  v-model="props.formulario.tipoDeEquipamento"
                  outlined dense hide-details clearable
                  @change="(v) => emits('selecionar-tipo-equipamento', v)"
                >
                  <template v-slot:item="{ item }">
                    <span><strong>{{ item.sigla || item.codigo || item.code }}</strong> – {{ item.descricao || item.nome || item.description || item.name }}</span>
                  </template>
                  <template v-slot:selection="{ item }">
                    <span><strong>{{ item.sigla || item.codigo || item.code }}</strong> – {{ item.descricao || item.nome || item.description || item.name }}</span>
                  </template>
                </v-autocomplete>
              </v-col>
              <v-col cols="12" md="3">
                <v-select
                  :items="TIPOS_DE_ORCAMENTO"
                  item-text="label"
                  item-value="value"
                  label="Tipo de Orçamento *"
                  v-model="props.formulario.tipoPropostaOrcamento"
                  :disabled="formularioBloqueado"
                  :rules="[(v) => !!v || 'Campo obrigatório']"
                  outlined dense hide-details
                />
              </v-col>
            </v-row>

            <v-row dense class="mt-3">
              <v-col cols="12" md="3">
                <v-select
                  v-if="!formularioBloqueado"
                  :items="opcoesEmpresa"
                  label="Empresa"
                  v-model="props.formulario.origem"
                  outlined dense hide-details
                  @change="(v) => emits('alterar-empresa', v)"
                />
                <v-text-field
                  v-else
                  label="Empresa"
                  v-model="props.formulario.origem"
                  readonly outlined dense hide-details class="so-readonly"
                />
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field
                  label="Status"
                  :value="props.formulario.statusDescription || props.formulario.status || ''"
                  readonly disabled outlined dense hide-details class="so-readonly"
                />
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field
                  label="Job"
                  v-model="props.formulario.jobNome"
                  readonly disabled outlined dense hide-details class="so-readonly"
                />
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field
                  label="Revisão Comercial"
                  :value="props.formulario.ultimaRevisao || props.formulario.revisao || 'ORIGINAL'"
                  readonly disabled outlined dense hide-details class="so-readonly"
                />
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field
                  label="Revisão Técnica"
                  :value="props.formulario.versionTec || 'ORIGINAL'"
                  readonly disabled outlined dense hide-details class="so-readonly"
                />
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field
                  label="Data Geração"
                  type="date"
                  v-model="props.formulario.dateGenerate"
                  readonly disabled outlined dense hide-details class="so-readonly"
                />
              </v-col>
            </v-row>

            <!-- SECAO: Cliente -->
            <v-expansion-panels v-model="panelCliente" flat multiple class="mt-4 so-panel">
              <v-expansion-panel>
                <v-expansion-panel-header class="so-panel__header">
                  <span class="subtitle-2 font-weight-medium">Cliente</span>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-row dense>
                    <v-col cols="12" md="6">
                      <v-autocomplete
                        :items="props.clientes"
                        :item-text="clienteItemText"
                        item-value="id"
                        :search-input="props.clienteBusca"
                        :loading="props.clientesCarregando"
                        no-filter
                        label="Cliente"
                        v-model="props.formulario.cliente"
                        outlined dense hide-details clearable
                        @focus="() => emits('buscar-clientes', props.clienteBusca || '')"
                        @update:search-input="(v) => emits('buscar-clientes', v)"
                        @change="(v) => emits('selecionar-cliente', v)"
                      />
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                        label="Referência do Cliente"
                        v-model="props.formulario.cliente_referencia"
                        outlined dense hide-details
                      />
                    </v-col>
                    <v-col cols="12" md="6" class="mt-3">
                      <v-text-field
                        label="CNPJ"
                        :value="props.formulario.clienteCnpj"
                        readonly disabled outlined dense hide-details class="so-readonly"
                      />
                    </v-col>
                    <v-col cols="12" md="6" class="mt-3" />
                    <v-col cols="12" md="6" class="mt-3">
                      <v-autocomplete
                        :items="props.vendedores"
                        item-text="nome"
                        item-value="id"
                        label="Vendedor"
                        v-model="props.formulario.vendedor"
                        outlined dense hide-details clearable
                      />
                    </v-col>
                    <v-col cols="12" md="6" class="mt-3">
                      <v-autocomplete
                        :items="props.orcamentistas"
                        item-text="nome"
                        item-value="id"
                        label="Orçamentista"
                        v-model="props.formulario.orcamentista"
                        outlined dense hide-details clearable
                      />
                    </v-col>
                    <v-col cols="12" md="6" class="mt-3">
                      <v-autocomplete
                        :items="props.contatosComerciaisCliente"
                        item-text="nome"
                        item-value="id"
                        label="Contato Comercial"
                        v-model="props.formulario.contato"
                        outlined dense hide-details clearable
                        :disabled="!props.formulario.cliente"
                        @change="(v) => emits('selecionar-contato', v)"
                      />
                    </v-col>
                    <v-col cols="12" md="6" class="mt-3">
                      <v-text-field
                        label="Contato Comercial Telefone"
                        v-model="props.formulario.contatoComercialTelefone"
                        readonly disabled outlined dense hide-details class="so-readonly"
                      />
                    </v-col>
                    <v-col cols="12" md="6" class="mt-3">
                      <v-text-field
                        label="Contato Comercial Celular"
                        v-model="props.formulario.contatoComercialCelular"
                        readonly disabled outlined dense hide-details class="so-readonly"
                      />
                    </v-col>
                    <v-col cols="12" md="6" class="mt-3">
                      <v-text-field
                        label="Contato Comercial E-mail"
                        v-model="props.formulario.contatoComercialEmail"
                        readonly disabled outlined dense hide-details class="so-readonly"
                      />
                    </v-col>
                    <v-col cols="12" md="6" class="mt-3">
                      <v-autocomplete
                        :items="props.contatosTecnicosCliente"
                        item-text="nome"
                        item-value="id"
                        label="Contato Técnico"
                        v-model="props.formulario.contatoTecnico"
                        outlined dense hide-details clearable
                        :disabled="!props.formulario.cliente"
                        @change="(v) => emits('selecionar-contato-tecnico', v)"
                      />
                    </v-col>
                    <v-col cols="12" md="6" class="mt-3">
                      <v-text-field
                        label="Contato Técnico Nome"
                        v-model="props.formulario.contatoTecnicoNome"
                        readonly disabled outlined dense hide-details class="so-readonly"
                      />
                    </v-col>
                    <v-col cols="12" md="6" class="mt-3">
                      <v-text-field
                        label="Contato Técnico Telefone"
                        v-model="props.formulario.contatoTecnicoTelefone"
                        readonly disabled outlined dense hide-details class="so-readonly"
                      />
                    </v-col>
                    <v-col cols="12" md="6" class="mt-3">
                      <v-text-field
                        label="Contato Técnico Celular"
                        v-model="props.formulario.contatoTecnicoCelular"
                        readonly disabled outlined dense hide-details class="so-readonly"
                      />
                    </v-col>
                    <v-col cols="12" md="6" class="mt-3">
                      <v-text-field
                        label="Contato Técnico E-mail"
                        v-model="props.formulario.contatoTecnicoEmail"
                        readonly disabled outlined dense hide-details class="so-readonly"
                      />
                    </v-col>
                  </v-row>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>

            <!-- SECAO: Proposta Técnica -->
            <v-expansion-panels v-model="panelTecnica" flat multiple class="mt-2 so-panel">
              <v-expansion-panel>
                <v-expansion-panel-header class="so-panel__header">
                  <span class="subtitle-2 font-weight-medium">Abertura de Solicitação de Orçamento Técnica</span>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-row dense class="mt-1">
                    <v-col cols="12">
                      <v-textarea
                        label="Descrição do Projeto"
                        v-model="props.formulario.projetoDescricao"
                        outlined dense hide-details
                        rows="3"
                        auto-grow
                        :readonly="formularioBloqueado"
                      />
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-text-field
                        label="Data Solicitação"
                        type="date"
                        v-model="props.formulario.dataSolicitacao"
                        outlined dense hide-details
                      />
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-text-field
                        label="Data Desejada"
                        type="date"
                        v-model="props.formulario.dataDesejada"
                        outlined dense hide-details
                      />
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-text-field
                        label="Data Conclusão"
                        type="date"
                        v-model="props.formulario.dataConclusao"
                        outlined dense hide-details
                      />
                    </v-col>
                  </v-row>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>

            <!-- SECAO: Proposta Comercial -->
            <v-expansion-panels v-model="panelComercial" flat multiple class="mt-2 so-panel">
              <v-expansion-panel>
                <v-expansion-panel-header class="so-panel__header">
                  <span class="subtitle-2 font-weight-medium">Abertura de Solicitação de Orçamento Comercial</span>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-row dense class="mt-1">
                    <v-col cols="12" md="3">
                      <v-text-field
                        label="Data Solicitação"
                        type="date"
                        v-model="props.formulario.dataSolicitacaoComercial"
                        outlined dense hide-details
                      />
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-text-field
                        label="Data Desejada"
                        type="date"
                        v-model="props.formulario.dataDesejadaComercial"
                        outlined dense hide-details
                      />
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-text-field
                        label="Data Conclusão"
                        type="date"
                        v-model="props.formulario.dataConclusaoComercial"
                        outlined dense hide-details
                      />
                    </v-col>
                  </v-row>
                  <v-row dense class="mt-1">
                    <v-col cols="12">
                      <span class="caption font-weight-medium grey--text text--darken-2">Tipo de Venda</span>
                      <v-radio-group
                        v-model="tipoOrcamentoVendaModel"
                        :disabled="formularioBloqueado"
                        row hide-details class="mt-1"
                      >
                        <v-radio
                          v-for="opcao in TIPOS_ORCAMENTO_VENDA"
                          :key="opcao.value"
                          :label="opcao.label"
                          :value="opcao.value"
                        />
                      </v-radio-group>
                      <span v-if="hasItens" class="caption grey--text">
                        Trocar o Tipo de Venda recalcula os itens já cadastrados
                      </span>
                    </v-col>
                  </v-row>
                  <!-- A pedido do usuário: ordem dos totais = Custo, Preço c/ICMS c/ISS s/IPI, Preço c/ICMS
                       c/ISS c/IPI, Valor ICMS, Valor IPI, Valor PIS, Valor COFINS, Preço Total Líquido —
                       e só depois a seção de Impostos de Serviço (inalterada). -->
                  <v-row dense class="mt-1">
                    <v-col cols="12" md="3">
                      <v-text-field
                        label="Custo Disp. s/ICMS, PIS, COFINS c/IPI"
                        v-model="props.formulario.valorDisponivel"
                        readonly
                        disabled
                        class="so-readonly"
                        outlined dense hide-details
                        :hint="hasItens ? 'Soma dos itens' : ''"
                        :persistent-hint="hasItens"
                      />
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-text-field
                        label="Preço de Venda c/ICMS c/ISS s/IPI"
                        v-model="props.formulario.precoVenda_sIPI_cICMS"
                        readonly
                        disabled
                        class="so-readonly"
                        outlined dense hide-details
                        :hint="hasItens ? 'Soma dos itens' : ''"
                        :persistent-hint="hasItens"
                      />
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-text-field
                        label="Preço de Venda c/ICMS c/ISS c/IPI"
                        v-model="props.formulario.precoVenda_cIPI_cICMS"
                        readonly
                        disabled
                        class="so-readonly"
                        outlined dense hide-details
                        :hint="hasItens ? 'Soma dos itens' : ''"
                        :persistent-hint="hasItens"
                      />
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-text-field
                        label="Valor do ICMS"
                        v-model="props.formulario.valorICMS"
                        readonly
                        disabled
                        class="so-readonly"
                        outlined dense hide-details
                        :hint="hasItens ? 'Soma dos itens' : ''"
                        :persistent-hint="hasItens"
                      />
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-text-field
                        label="Valor do IPI"
                        v-model="props.formulario.valorIPI"
                        readonly
                        disabled
                        class="so-readonly"
                        outlined dense hide-details
                        :hint="hasItens ? 'Soma dos itens' : ''"
                        :persistent-hint="hasItens"
                      />
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-text-field
                        label="Valor do PIS"
                        v-model="props.formulario.valorPIS"
                        readonly
                        disabled
                        class="so-readonly"
                        outlined dense hide-details
                        :hint="hasItens ? 'Soma dos itens' : ''"
                        :persistent-hint="hasItens"
                      />
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-text-field
                        label="Valor do COFINS"
                        v-model="props.formulario.valorCOFINS"
                        readonly
                        disabled
                        class="so-readonly"
                        outlined dense hide-details
                        :hint="hasItens ? 'Soma dos itens' : ''"
                        :persistent-hint="hasItens"
                      />
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-text-field
                        label="Preço Total Líquido"
                        v-model="props.formulario.precoTotalLiquido"
                        readonly
                        disabled
                        class="so-readonly"
                        outlined dense hide-details
                        :hint="hasItens ? 'Soma dos itens' : ''"
                        :persistent-hint="hasItens"
                      />
                    </v-col>
                  </v-row>
                  <v-row v-if="hasItensServico" dense class="mt-1">
                    <v-col cols="12">
                      <span class="caption font-weight-medium grey--text text--darken-2">Impostos de Serviço (ISS/IRRF/CSLL/INSS)</span>
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-text-field
                        label="Valor do ISS"
                        v-model="props.formulario.valorISS"
                        readonly disabled class="so-readonly" outlined dense hide-details hint="Soma dos itens" persistent-hint
                      />
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-text-field
                        label="Valor do IRRF"
                        v-model="props.formulario.valorIRRF"
                        readonly disabled class="so-readonly" outlined dense hide-details hint="Soma dos itens" persistent-hint
                      />
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-text-field
                        label="Valor do CSLL"
                        v-model="props.formulario.valorCSLL"
                        readonly disabled class="so-readonly" outlined dense hide-details hint="Soma dos itens" persistent-hint
                      />
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-text-field
                        label="Valor do INSS"
                        v-model="props.formulario.valorINSS"
                        readonly disabled class="so-readonly" outlined dense hide-details hint="Soma dos itens" persistent-hint
                      />
                    </v-col>
                  </v-row>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>

            <!-- SECAO: Itens da Solicitação -->
            <v-expansion-panels v-model="panelItens" flat multiple class="mt-2 so-panel">
              <v-expansion-panel>
                <v-expansion-panel-header class="so-panel__header">
                  <span class="subtitle-2 font-weight-medium">
                    Itens da Solicitação
                    <v-chip v-if="hasItens" x-small color="primary" text-color="white" class="ml-2">
                      {{ (props.formulario.itens || []).length }}
                    </v-chip>
                  </span>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <div class="d-flex align-center mb-2" style="gap:8px">
                    <v-btn
                      v-if="podeEditarItens"
                      icon small color="primary"
                      :disabled="!podeAdicionarItem"
                      @click="abrirNovoItem()"
                    >
                      <v-icon>mdi-plus</v-icon>
                    </v-btn>
                    <span v-if="podeEditarItens && !podeAdicionarItem" class="caption error--text">
                      Selecione o Tipo de Venda para incluir itens
                    </span>
                    <span v-if="hasItens" class="caption grey--text">
                      Os valores de abertura são calculados automaticamente a partir dos itens
                    </span>
                  </div>
                  <v-data-table
                    :headers="headersItens"
                    :items="props.formulario.itens || []"
                    dense hide-default-footer hide-default-header class="elevation-0"
                    no-data-text="Nenhum item adicionado"
                  >
                    <template v-slot:header>
                      <thead class="v-data-table-header">
                        <tr>
                          <th class="text-left" style="width:90px;">Ações</th>
                          <th class="text-left" style="min-width:240px;">Descrição</th>
                          <th class="text-left" style="width:200px;">Tipo de Equipamento</th>
                          <th class="text-left" style="width:80px;">Qtd</th>
                          <th class="text-left" style="width:120px;">Custo Unit.</th>
                          <th class="text-left" style="width:120px;">Custo Total</th>
                          <th class="text-left" style="width:150px;">Preço de Venda Unitário</th>
                          <th class="text-left" style="width:135px;">Preço de Venda Total</th>
                          <th class="text-left" style="width:105px;">%IPI</th>
                          <th class="text-left" style="width:115px;">%ICMS</th>
                          <th class="text-left" style="width:85px;">%PIS</th>
                          <th class="text-left" style="width:95px;">%COFINS</th>
                          <th class="text-left" style="width:100px;">%ISS</th>
                          <th class="text-left" style="width:90px;">KI</th>
                          <th class="text-left" style="width:90px;">KV</th>
                        </tr>
                      </thead>
                    </template>
                    <template v-slot:item="{ item, index }">
                      <tr>
                        <td>
                          <div v-if="podeEditarItens" class="d-flex align-center" style="gap:4px;">
                            <v-btn
                              icon x-small color="primary"
                              @click="abrirEditarItem(item, index)"
                            >
                              <v-icon small>mdi-pencil</v-icon>
                            </v-btn>
                            <v-btn
                              v-if="podeExcluirItens"
                              icon x-small color="error"
                              @click="emits('remove-item', index)"
                            >
                              <v-icon small>mdi-delete</v-icon>
                            </v-btn>
                          </div>
                          <div v-else class="d-flex align-center" style="gap:4px;">
                            <v-btn
                              icon x-small color="info"
                              @click="abrirVisualizarItem(item, index)"
                            >
                              <v-icon small>mdi-eye</v-icon>
                            </v-btn>
                          </div>
                        </td>
                        <td class="so-item-descricao">{{ montarDescricaoItemGrid(item) }}</td>
                        <td>{{ montarTipoEquipamentoItemGrid(item) }}</td>
                        <td>{{ item.quantidade }}</td>
                        <td>{{ formatarValorNumero(item.valorDisponivel) }}</td>
                        <td>{{ formatarValorDisponivelTotalNumero(item) }}</td>
                        <td>{{ formatarValorNumero(item.precoVenda_sIPI_cICMS) }}</td>
                        <td class="font-weight-medium">{{ formatarValorTotalNumero(item) }}</td>
                        <td>{{ formatarPercentualGrid(item.pocentagemIPIVenda) }}</td>
                        <td>{{ formatarPercentualGrid(item.porcentagemICMSVenda) }}</td>
                        <td>{{ formatarPercentualGrid(item.porcentagemPIS) }}</td>
                        <td>{{ formatarPercentualGrid(item.porcentagemCOFINS) }}</td>
                        <td>{{ formatarPercentualGrid(item.porcentagemISS) }}</td>
                        <td>{{ formatarKiNumero(item.ki) }}</td>
                        <td>{{ formatarKvNumero(item.kv) }}</td>
                      </tr>
                    </template>
                    <template v-slot:no-data>
                      <div class="pa-3 text-center grey--text">Nenhum item adicionado</div>
                    </template>
                  </v-data-table>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>

            <!-- SECAO: Observações -->
            <v-expansion-panels v-model="panelObservacoes" flat multiple class="mt-2 so-panel">
              <v-expansion-panel>
                <v-expansion-panel-header class="so-panel__header">
                  <span class="subtitle-2 font-weight-medium">Observações</span>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-row dense class="mt-1">
                    <v-col cols="12">
                      <v-select
                        :items="niveisProposta"
                        :item-text="nivelPropostaItemText"
                        item-value="numero"
                        label="Nível da Proposta"
                        v-model="props.formulario.nivel"
                        outlined dense hide-details clearable
                      />
                    </v-col>
                    <v-col cols="12">
                      <v-textarea
                        label="Observações"
                        v-model="props.formulario.observacao"
                        outlined dense hide-details
                        rows="3"
                        auto-grow
                        :readonly="formularioBloqueado"
                      />
                    </v-col>
                  </v-row>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>

            <!-- SECAO: Follow-Up -->
            <v-expansion-panels v-model="panelFollowUp" flat multiple class="mt-2 so-panel">
              <v-expansion-panel>
                <v-expansion-panel-header class="so-panel__header">
                  <span class="subtitle-2 font-weight-medium">Follow-Up</span>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <div class="d-flex align-center mb-2" style="gap:8px">
                    <v-btn v-if="podeIncluirFollowUp" icon small color="primary" @click="abrirNovoFollowUp()">
                      <v-icon>mdi-plus</v-icon>
                    </v-btn>
                    <v-text-field placeholder="Search" dense outlined hide-details style="max-width:220px" />
                  </div>
                  <v-data-table
                    :headers="headersFollowUp"
                    :items="props.followUps"
                    dense hide-default-footer class="elevation-0"
                    no-data-text="No data available"
                  >
                    <template v-slot:item.data="{ item }">
                      {{ formatarData(item.data) }}
                    </template>
                  </v-data-table>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>

            <!-- SECAO: Revisões -->
            <v-expansion-panels v-model="panelRevisoes" flat class="mt-2 so-panel">
              <v-expansion-panel>
                <v-expansion-panel-header class="so-panel__header">
                  <span class="subtitle-2 font-weight-medium">Revisões</span>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <div v-if="props.revisoesFallbackDisponivel" class="d-flex align-center mb-2" style="gap:8px">
                    <v-btn
                      small outlined color="primary"
                      :loading="props.revisoesFallbackCarregando"
                      :disabled="props.revisoesFallbackCarregando"
                      @click="emits('buscar-historico-completo')"
                    >
                      Buscar histórico completo
                    </v-btn>
                    <span class="caption grey--text">Poucas revisões encontradas na busca rápida — clique para uma busca mais ampla (pode demorar alguns segundos).</span>
                  </div>
                  <v-data-table
                    :headers="headersRevisoes"
                    :items="props.revisoes"
                    dense hide-default-footer class="elevation-0"
                    no-data-text="No data available"
                  >
                    <template v-slot:item.dateGenerate="{ item }">
                      {{ formatarData(item.dateGenerate) }}
                    </template>
                    <template v-slot:item.dataDesejadaComercial="{ item }">
                      {{ item.dataDesejadaComercial ? formatarData(item.dataDesejadaComercial) : '-' }}
                    </template>
                    <template v-slot:item.dataDesejada="{ item }">
                      {{ item.dataDesejada ? formatarData(item.dataDesejada) : '-' }}
                    </template>
                  </v-data-table>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>

            </fieldset>

        </div>
      </div>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogNovoItem" width="calc(100vw - 32px)" max-width="none" persistent :retain-focus="false">
      <v-card>
        <v-card-title class="headline">{{ tituloDialogItem }}</v-card-title>
        <v-card-text class="pt-4">
          <fieldset class="so-item-fieldset" :disabled="itemSomenteLeitura">
          <v-alert v-if="novoItemErros.percentuais" type="error" dense outlined class="mb-3">
            {{ novoItemErros.percentuais }}
          </v-alert>
          <v-row dense>
            <!-- Tipo de Equipamento / Descrição -->
            <v-col cols="12" md="6">
              <v-autocomplete
                label="Tipo de Equipamento"
                v-model="novoItemForm.tipoDeEquipamento"
                :items="itensTiposDeEquipamentoFiltrados"
                :loading="itensTiposDeEquipamentoCarregando"
                item-text="descricao"
                item-value="id"
                outlined
                dense
                hide-details="auto"
                :error-messages="novoItemErros.tipoDeEquipamento"
                @change="aoSelecionarTipoDeEquipamentoItem"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                label="Descrição"
                v-model="novoItemForm.descricao"
                outlined
                dense
                hide-details="auto"
                :error-messages="novoItemErros.descricao"
              />
            </v-col>
            <v-col cols="12" class="item-row-break" />

            <!-- Tipo de Serviço (só aparece se Serviço) / NCM (só aparece se Produto) -->
            <v-col v-if="itemEmEdicaoEhServico" cols="12" md="6">
              <v-autocomplete
                label="Tipo de Serviço"
                v-model="novoItemForm.tipoDeServico"
                :items="itensTiposDeServico"
                :loading="itensTiposDeServicoCarregando"
                item-text="servicoLabel"
                item-value="id"
                :disabled="desativaTipoDeServicoItem"
                outlined
                dense
                hide-details="auto"
                :error-messages="novoItemErros.tipoDeServico"
                @change="aoSelecionarTipoDeServicoItem"
              >
                <template v-slot:item="{ item }">
                  <span>{{ item.servicoCodigo }} - {{ item.servicoDescricao }}</span>
                </template>
                <template v-slot:selection="{ item }">
                  <span>{{ item.servicoCodigo }} - {{ item.servicoDescricao }}</span>
                </template>
              </v-autocomplete>
            </v-col>
            <v-col v-else cols="12" md="6">
              <v-autocomplete
                label="NCM"
                v-model="novoItemForm.ncm"
                :items="itensNcm"
                :loading="itensNcmCarregando"
                item-text="descricaoCompleta"
                item-value="id"
                :disabled="campoNcmDesabilitado"
                outlined
                dense
                hide-details="auto"
                :error-messages="novoItemErros.ncm"
                @change="aoSelecionarNcmItem"
              />
            </v-col>
            <v-col cols="12" class="item-row-break" />

            <!-- Quantidade / Custo -->
            <v-col cols="12" md="3">
              <v-text-field
                label="Qtd"
                v-model="novoItemForm.quantidade"
                type="number"
                :disabled="camposBasicosItemDesabilitados"
                outlined
                dense
                hide-details="auto"
                :error-messages="novoItemErros.quantidade"
                @input="recalcularNovoItemForm()"
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                label="Custo Disp. s/ICMS, PIS, COFINS c/IPI"
                :value="novoItemForm.valorDisponivel"
                :disabled="camposBasicosItemDesabilitados"
                outlined
                dense
                hide-details
                @input="(v) => aoDigitarMoedaNovoItem('valorDisponivel', v)"
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                label="Custo Total (Qtd × Valor Disponível)"
                :value="novoItemReadonly.valorDisponivelTotal"
                outlined
                dense
                hide-details
                readonly
                disabled
                class="so-readonly"
              />
            </v-col>
            <v-col cols="12" class="item-row-break" />

            <!-- Preço de Venda (produto: Com ICMS e Sem IPI / serviço: ISS) -->
            <v-col cols="12" md="3">
              <v-text-field
                :label="itemEmEdicaoEhServico ? 'Preço de Venda &quot;ISS&quot;' : 'Preço de Venda &quot;Com ICMS e Sem IPI&quot;'"
                :value="novoItemForm.precoVenda_sIPI_cICMS"
                :disabled="camposBasicosItemDesabilitados"
                outlined
                dense
                hide-details
                @input="(v) => aoDigitarMoedaNovoItem('precoVenda_sIPI_cICMS', v)"
              />
            </v-col>
            <v-col v-if="!itemEmEdicaoEhServico" cols="12" md="3">
              <v-text-field
                label="Preço de Venda &quot;Com ICMS e Com IPI&quot;"
                :value="novoItemReadonly.precoVenda_cIPI_cICMS"
                outlined
                dense
                hide-details
                readonly
                disabled
                class="so-readonly"
              />
            </v-col>
            <v-col v-if="!itemEmEdicaoEhServico" cols="12" md="3">
              <v-text-field
                label="Preço de Venda Total &quot;Com ICMS e Sem IPI&quot;"
                :value="novoItemReadonly.vlrTotal"
                outlined
                dense
                hide-details
                readonly
                disabled
                class="so-readonly"
              />
            </v-col>
            <v-col cols="12" class="item-row-break" />

            <!-- Preço Total Líquido -->
            <v-col cols="12" md="3">
              <v-text-field
                label="Preço Total Líquido"
                :value="novoItemReadonly.precoTotalLiquido"
                outlined dense hide-details readonly disabled class="so-readonly"
              />
            </v-col>
            <v-col cols="12" class="item-row-break" />

            <!-- IPI -->
            <v-col cols="12" md="3">
              <v-text-field
                label="% IPI"
                :value="novoItemForm.pocentagemIPIVenda"
                :disabled="campoProdutoDesabilitado"
                :rules="[regraPercentual]"
                outlined
                dense
                hide-details="auto"
                @input="(v) => aoDigitarPercentual('pocentagemIPIVenda', v)"
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                label="Valor IPI"
                :value="novoItemReadonly.valorIPI"
                outlined
                dense
                hide-details
                readonly
                disabled
                class="so-readonly"
              />
            </v-col>
            <v-col cols="12" class="item-row-break" />

            <!-- ICMS -->
            <v-col cols="12" md="3">
              <v-text-field
                label="% Base de Cálculo do ICMS"
                :value="novoItemForm.porcentagemBaseCalculoICMS"
                :disabled="campoProdutoDesabilitado"
                :rules="[regraPercentual]"
                outlined dense hide-details="auto"
                @input="(v) => aoDigitarPercentual('porcentagemBaseCalculoICMS', v)"
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                label="Base de Cálculo do ICMS"
                :value="novoItemReadonly.baseCalculoICMS"
                outlined dense hide-details readonly disabled class="so-readonly"
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                label="% ICMS"
                :value="novoItemForm.porcentagemICMSVenda"
                :disabled="campoProdutoDesabilitado"
                :rules="[regraPercentual]"
                outlined
                dense
                hide-details="auto"
                @input="(v) => aoDigitarPercentual('porcentagemICMSVenda', v)"
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                label="Valor ICMS"
                :value="novoItemReadonly.valorICMS"
                outlined
                dense
                hide-details
                readonly
                disabled
                class="so-readonly"
              />
            </v-col>
            <v-col cols="12" class="item-row-break" />

            <!-- PIS -->
            <v-col cols="12" md="3">
              <v-text-field
                label="% Base de Cálculo do PIS"
                :value="novoItemForm.porcentagemBaseCalculoPIS"
                :disabled="campoServicoDesabilitado"
                :rules="[regraPercentual]"
                outlined dense hide-details="auto"
                @input="(v) => aoDigitarPercentual('porcentagemBaseCalculoPIS', v)"
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                label="Base de Cálculo do PIS"
                :value="novoItemReadonly.baseCalculoPIS"
                outlined dense hide-details readonly disabled class="so-readonly"
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                label="% PIS"
                :value="novoItemForm.porcentagemPIS"
                :disabled="campoServicoDesabilitado || pisCofinsIsentosPorRecap"
                :hint="pisCofinsIsentosPorRecap ? 'Isento no tipo de venda Recap' : ''"
                persistent-hint
                :rules="[regraPercentual]"
                outlined
                dense
                hide-details="auto"
                @input="(v) => aoDigitarPercentual('porcentagemPIS', v)"
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                label="Valor do PIS"
                :value="novoItemReadonly.valorPIS"
                outlined dense hide-details readonly disabled class="so-readonly"
              />
            </v-col>
            <v-col cols="12" class="item-row-break" />

            <!-- COFINS -->
            <v-col cols="12" md="3">
              <v-text-field
                label="% Base de Cálculo do COFINS"
                :value="novoItemForm.porcentagemBaseCalculoCOFINS"
                :disabled="campoServicoDesabilitado"
                :rules="[regraPercentual]"
                outlined dense hide-details="auto"
                @input="(v) => aoDigitarPercentual('porcentagemBaseCalculoCOFINS', v)"
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                label="Base de Cálculo do COFINS"
                :value="novoItemReadonly.baseCalculoCOFINS"
                outlined dense hide-details readonly disabled class="so-readonly"
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                label="% COFINS"
                :value="novoItemForm.porcentagemCOFINS"
                :disabled="campoServicoDesabilitado || pisCofinsIsentosPorRecap"
                :hint="pisCofinsIsentosPorRecap ? 'Isento no tipo de venda Recap' : ''"
                persistent-hint
                :rules="[regraPercentual]"
                outlined
                dense
                hide-details="auto"
                @input="(v) => aoDigitarPercentual('porcentagemCOFINS', v)"
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                label="Valor do COFINS"
                :value="novoItemReadonly.valorCOFINS"
                outlined dense hide-details readonly disabled class="so-readonly"
              />
            </v-col>
            <v-col cols="12" class="item-row-break" />

            <!-- ISS -->
            <v-col cols="12" md="3">
              <v-text-field
                label="% Base de Cálculo do ISS"
                :value="novoItemForm.porcentagemBaseCalculoISS"
                :disabled="campoServicoDesabilitado"
                :rules="[regraPercentual]"
                outlined dense hide-details="auto"
                @input="(v) => aoDigitarPercentual('porcentagemBaseCalculoISS', v)"
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                label="Base de Cálculo do ISS"
                :value="novoItemReadonly.baseCalculoISS"
                outlined dense hide-details readonly disabled class="so-readonly"
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                label="% ISS"
                :value="novoItemForm.porcentagemISS"
                :disabled="campoServicoDesabilitado"
                :rules="[regraPercentual]"
                outlined
                dense
                hide-details="auto"
                @input="(v) => aoDigitarPercentual('porcentagemISS', v)"
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                label="Valor do ISS"
                :value="novoItemReadonly.valorISS"
                outlined dense hide-details readonly disabled class="so-readonly"
              />
            </v-col>
            <v-col cols="12" class="item-row-break" />

            <!-- IRRF -->
            <v-col cols="12" md="3">
              <v-text-field
                label="% Base de Cálculo do IRRF"
                :value="novoItemForm.porcentagemBaseCalculoIRRF"
                :disabled="campoServicoDesabilitado"
                :rules="[regraPercentual]"
                outlined dense hide-details="auto"
                @input="(v) => aoDigitarPercentual('porcentagemBaseCalculoIRRF', v)"
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                label="Base de Cálculo do IRRF"
                :value="novoItemReadonly.baseCalculoIRRF"
                outlined dense hide-details readonly disabled class="so-readonly"
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                label="% IRRF"
                :value="novoItemForm.porcentagemIRRF"
                :disabled="campoServicoDesabilitado"
                :rules="[regraPercentual]"
                outlined dense hide-details="auto"
                @input="(v) => aoDigitarPercentual('porcentagemIRRF', v)"
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                label="Valor do IRRF"
                :value="novoItemReadonly.valorIRRF"
                outlined dense hide-details readonly disabled class="so-readonly"
              />
            </v-col>
            <v-col cols="12" class="item-row-break" />

            <!-- CSLL -->
            <v-col cols="12" md="3">
              <v-text-field
                label="% Base de Cálculo do CSLL"
                :value="novoItemForm.porcentagemBaseCalculoCSLL"
                :disabled="campoServicoDesabilitado"
                :rules="[regraPercentual]"
                outlined dense hide-details="auto"
                @input="(v) => aoDigitarPercentual('porcentagemBaseCalculoCSLL', v)"
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                label="Base de Cálculo do CSLL"
                :value="novoItemReadonly.baseCalculoCSLL"
                outlined dense hide-details readonly disabled class="so-readonly"
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                label="% CSLL"
                :value="novoItemForm.porcentagemCSLL"
                :disabled="campoServicoDesabilitado"
                :rules="[regraPercentual]"
                outlined dense hide-details="auto"
                @input="(v) => aoDigitarPercentual('porcentagemCSLL', v)"
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                label="Valor do CSLL"
                :value="novoItemReadonly.valorCSLL"
                outlined dense hide-details readonly disabled class="so-readonly"
              />
            </v-col>
            <v-col cols="12" class="item-row-break" />

            <!-- INSS -->
            <v-col cols="12" md="3">
              <v-text-field
                label="% Base de Cálculo do INSS"
                :value="novoItemForm.porcentagemBaseCalculoINSS"
                :disabled="campoServicoDesabilitado"
                :rules="[regraPercentual]"
                outlined dense hide-details="auto"
                @input="(v) => aoDigitarPercentual('porcentagemBaseCalculoINSS', v)"
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                label="Base de Cálculo do INSS"
                :value="novoItemReadonly.baseCalculoINSS"
                outlined dense hide-details readonly disabled class="so-readonly"
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                label="% INSS"
                :value="novoItemForm.porcentagemINSS"
                :disabled="campoServicoDesabilitado"
                :rules="[regraPercentual]"
                outlined dense hide-details="auto"
                @input="(v) => aoDigitarPercentual('porcentagemINSS', v)"
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                label="Valor do INSS"
                :value="novoItemReadonly.valorINSS"
                outlined dense hide-details readonly disabled class="so-readonly"
              />
            </v-col>
            <v-col cols="12" class="item-row-break" />

            <!-- KI / KI do Custo / KV -->
            <v-col cols="12" md="3">
              <v-text-field
                label="KI"
                :value="novoItemForm.ki"
                :disabled="camposBasicosItemDesabilitados"
                outlined
                dense
                hide-details
                @input="aoDigitarKi"
                @blur="aoSairDoKi"
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                label="KI do Custo"
                :value="novoItemReadonly.kidoCusto"
                outlined
                dense
                hide-details
                readonly
                disabled
                class="so-readonly"
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                label="KV"
                :value="novoItemReadonly.kv"
                :error-messages="novoItemErroKvExibido"
                outlined
                dense
                hide-details="auto"
                readonly
                disabled
                class="so-readonly"
                :class="{ 'so-erro-forcado': !!novoItemErroKvExibido }"
              />
            </v-col>
          </v-row>
          </fieldset>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text type="button" @click.stop="cancelarNovoItem">{{ modoVisualizacaoItem ? 'Fechar' : 'Cancelar' }}</v-btn>
          <v-btn v-if="!modoVisualizacaoItem" color="primary" dark type="button" @click.stop="adicionarNovoItem">Adicionar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogConfirmarFechamentoCancelado" max-width="700px" persistent>
      <v-card>
        <v-card-title class="headline">Atenção</v-card-title>
        <v-card-text>
          Esta Solicitação tem como último follow up cadastrado com o status CANCELADO, ao fechar a solicitação não poderá mais ser editada.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="cancelarFechamentoCancelado">Cancelar</v-btn>
          <v-btn color="error" dark @click="confirmarFechamentoCancelado">Confirmar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogConfirmarConclusao" max-width="700px" persistent>
      <v-card>
        <v-card-title class="headline">Atenção</v-card-title>
        <v-card-text>
          Ao Concluir uma Solicitação de Orçamento ela só poderá ser Alterada por uma Revisão!
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="cancelarConfirmacaoConclusao">Cancelar</v-btn>
          <v-btn color="success" dark @click="confirmarConclusaoSolicitacao">Concluir</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogConfirmarTrocaTipoVenda" max-width="600px" persistent>
      <v-card>
        <v-card-title class="headline">Atenção</v-card-title>
        <v-card-text>
          Ao trocar o Tipo de Venda, os cálculos dos itens já cadastrados serão recalculados de acordo com o novo tipo. Deseja continuar?
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="cancelarTrocaTipoVenda">Cancelar</v-btn>
          <v-btn color="warning" dark @click="confirmarTrocaTipoVenda">Continuar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogSelecionarTipoRevisao" max-width="600px" persistent>
      <v-card>
        <v-card-title class="headline">Tipo de Revisão</v-card-title>
        <v-card-text>
          <v-radio-group v-model="tipoRevisaoSelecionado" column>
            <v-radio label="Comercial" value="1" />
            <v-radio label="Técnica" value="2" />
            <v-radio label="Comercial e Técnica" value="3" />
          </v-radio-group>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="cancelarSelecaoTipoRevisao">Cancelar</v-btn>
          <v-btn color="primary" dark :disabled="!tipoRevisaoSelecionado" @click="confirmarSelecaoTipoRevisao">
            Continuar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: Follow-Up -->
    <v-dialog v-model="dialogFollowUp" max-width="560px" persistent>
      <v-card>
        <v-card-title class="primary white--text">
          <v-icon left color="white">mdi-message-text-outline</v-icon>
          Novo Follow-Up
        </v-card-title>
        <v-card-text class="pt-5">
          <v-row dense>
            <v-col cols="12">
              <v-textarea
                label="Descricao"
                v-model="followUpForm.description"
                outlined dense rows="3" auto-grow :error-messages="followUpErros.description"
              />
            </v-col>
            <v-col cols="12" md="6" class="mt-3">
              <v-text-field
                label="Data"
                type="date"
                v-model="followUpForm.data"
                readonly outlined dense hide-details class="so-readonly"
              />
            </v-col>
            <v-col cols="12" md="6" class="mt-3">
              <v-autocomplete
                :items="props.contatosComerciaisCliente"
                item-text="nome"
                item-value="nome"
                label="Contato Comercial"
                v-model="followUpForm.contato_comercial"
                outlined dense clearable :error-messages="followUpErros.contato_comercial"
              />
            </v-col>
            <v-col v-if="!statusFormularioConcluido" cols="12" class="mt-3">
              <v-select
                :items="statusOpcoesFollowUp"
                item-text="description"
                item-value="id"
                label="Status"
                v-model="followUpForm.status"
                outlined dense clearable :error-messages="followUpErros.status"
              />
            </v-col>
            <v-col v-else cols="12" class="mt-3">
              <v-text-field
                label="Status"
                :value="descricaoStatusConcluidoFollowUp"
                readonly
                outlined
                dense
                hide-details
                class="so-readonly"
              />
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions class="px-6 pb-4">
          <v-spacer />
          <v-btn text @click="dialogFollowUp = false">Cancelar</v-btn>
          <v-btn color="primary" :loading="props.carregando" @click="confirmarFollowUp()">
            Salvar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: Historico Item -->
    <v-dialog v-model="historicoDialog" max-width="800px">
      <v-card>
        <v-card-title class="primary white--text">
          <v-icon left color="white">mdi-history</v-icon>
          Histórico do Item
        </v-card-title>
        <v-card-text class="pt-5">
          <div v-if="historicoItemSelected" class="mb-4">
            <p class="mb-2"><strong>Descrição:</strong> {{ historicoItemSelected.descricao }}</p>
            <p class="mb-2"><strong>Quantidade:</strong> {{ historicoItemSelected.quantidade }}</p>
            <p class="mb-2"><strong>Unidade:</strong> {{ historicoItemSelected.unidade }}</p>
            <p><strong>Observação:</strong> {{ historicoItemSelected.observacao || '-' }}</p>
          </div>
          <v-divider class="my-3" />
          <div v-if="historicoLoading" class="d-flex justify-center align-center" style="min-height: 200px">
            <v-progress-circular indeterminate color="primary" />
          </div>
          <div v-else-if="historicoItems.length === 0" class="pa-3 text-center text-muted">
            <v-icon class="mr-2">mdi-information</v-icon>
            Nenhum histórico disponível
          </div>
          <v-data-table
            v-else
            :headers="[
              { text: 'Data/Hora', value: 'data', sortable: false },
              { text: 'Descrição', value: 'descricao', sortable: false },
              { text: 'Status', value: 'status', sortable: false },
              { text: 'Quantidade', value: 'quantidade', sortable: false },
              { text: 'Responsável', value: 'responsavel', sortable: false }
            ]"
            :items="historicoItems"
            dense hide-default-footer class="elevation-0"
            no-data-text="No data available"
          >
            <template v-slot:item.data="{ item }">
              {{ item.data ? new Date(item.data).toLocaleString('pt-BR') : '-' }}
            </template>
          </v-data-table>
        </v-card-text>
        <v-card-actions class="px-6 pb-4">
          <v-spacer />
          <v-btn text @click="historicoDialog = false">Fechar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: Novo Contato -->
    <v-dialog v-model="abrirNovoContatoModel" max-width="650px" persistent>
      <v-card>
        <v-card-title class="primary white--text">
          <v-icon left color="white">mdi-account-plus</v-icon>
          {{ tipoContatoDialog === 'tecnico' ? 'Novo contato técnico' : 'Novo contato comercial' }}
        </v-card-title>
        <v-card-text class="pt-6">
          <v-row>
            <v-col cols="12" md="6">
              <v-text-field label="Nome" v-model="props.novoContato.nome" outlined dense />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field label="E-mail" v-model="props.novoContato.email" outlined dense />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field label="Telefone" v-model="props.novoContato.telefone" outlined dense />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field label="Celular" v-model="props.novoContato.celular" outlined dense />
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions class="px-6 pb-4">
          <v-spacer />
          <v-btn text @click="abrirNovoContatoModel = false">Cancelar</v-btn>
          <v-btn color="primary" :loading="props.carregando" @click="emits('criar-contato', { tipo: tipoContatoDialog })">
            Salvar contato
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>

  </template>

<style scoped>
.so-modal {
  background: #fafafa;
}

.item-row-break {
  flex: 0 0 100%;
  max-width: 100%;
  height: 0;
  min-height: 0;
  padding: 0 !important;
}

.so-topbar {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 12px;
  padding: 8px 16px;
  /* BUGFIX (a pedido do usuário): título "Solicitação de Orçamento" aparecia apagado/invisível no topo
     da tela. A causa é que o projeto não habilita `customProperties` no Vuetify (src/plugins/vuetify.js),
     então a variável CSS --v-primary-base nunca é gerada pelo framework — a barra ficava sem cor de
     fundo (transparente/branca) enquanto o texto do título continuava fixo em branco. Usa-se aqui a cor
     primary real (#1976D2, a mesma aplicada nos botões desta tela) como fallback sólido. */
  background-color: var(--v-primary-base, #1976d2);
  color: white;
  position: sticky;
  top: 0;
  z-index: 10;
}

.so-topbar__left-actions {
  display: flex;
  gap: 8px;
  align-items: center;
  min-width: 0;
  flex-wrap: nowrap;
}

.so-topbar__title {
  margin: 0;
  text-align: center;
  font-size: 1.15rem;
  font-weight: 600;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.so-topbar__title .v-icon {
  color: #ffffff !important;
}

.so-topbar__actions {
  display: flex;
  gap: 8px;
  flex-wrap: nowrap;
  justify-content: flex-end;
  overflow-x: auto;
  overflow-y: hidden;
  min-width: 0;
  scrollbar-width: thin;
}

.so-topbar__btn {
  width: 136px;
  min-width: 136px !important;
  white-space: nowrap;
  flex: 0 0 136px;
}

.so-topbar__btn--auto-width {
  width: auto !important;
  min-width: 0 !important;
  max-width: 100%;
  white-space: nowrap !important;
  padding-left: 16px;
  padding-right: 16px;
  box-sizing: border-box;
  overflow: visible;
  text-overflow: initial;
  flex: 0 0 auto;
}

.so-topbar__btn--close {
  width: 96px;
  min-width: 96px !important;
  flex-basis: 96px;
}

.so-topbar__btn--proposal {
  width: 224px;
  min-width: 224px !important;
  flex-basis: 224px;
}

.so-body {
  height: calc(100vh - 60px);
  overflow: auto;
  overflow-x: hidden;
}

.so-form {
  padding: 20px 24px;
}

.so-fieldset {
  border: 0;
  margin: 0;
  padding: 0;
  min-width: 0;
}

.so-panel {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
}

.so-panel .so-panel__header {
  background: #f5f5f5;
  min-height: 40px !important;
}

.so-readonly .v-input__slot {
  background: #f5f5f5 !important;
}

.so-readonly input,
.so-readonly textarea,
.so-form--readonly input,
.so-form--readonly textarea {
  color: rgba(0, 0, 0, 0.87) !important;
  -webkit-text-fill-color: rgba(0, 0, 0, 0.87) !important;
}

.so-readonly .v-label,
.so-form--readonly .v-label {
  color: rgba(0, 0, 0, 0.6) !important;
}

/* BUGFIX (a pedido do usuário): campos calculados (KV e demais readonly/disabled com error-messages)
   ficam com a mensagem de validação na cor cinza padrão do Vuetify, deixando o alerta (ex.: "KV deve
   ser maior que zero") quase invisível mesmo com o valor zerado/inválido. O motivo é que, no Vuetify 2,
   o computed "hasState" do v-input (responsável por aplicar a classe error--text) ignora o estado de
   erro quando o campo está "disabled" — então a classe error--text nunca chega a ser aplicada, mesmo
   com error-messages preenchido. Por isso não dá pra depender de .error--text aqui: cada campo marca
   sozinho, via :class, a classe "so-erro-forcado" quando sua mensagem de erro estiver ativa. O texto da
   mensagem em si (.v-messages__message) é renderizado pelo componente interno VMessages do Vuetify —
   um filho de verdade, não parte do template deste componente —, então o CSS "scoped" desta SFC não
   alcança esse nó. Tentei ::v-deep aqui mesmo (dentro do <style scoped>), mas o vue-loader deste
   projeto ainda assim injetou o atributo de escopo no seletor descendente (.v-messages/.v-messages__message
   ficaram com [data-v-xxx], quando o esperado era só o .so-erro-forcado ter o atributo) — resultado:
   a regra não batia com nada e a cor cinza continuava. A regra de verdade está no <style> (sem scoped)
   no fim do arquivo, que não sofre esse problema. Reaproveitável em qualquer outro campo
   calculado/desabilitado com validação (não só o KV) — ver definição completa no <style> global abaixo. */

.so-campo-obrigatorio .v-input__slot {
  border-color: #ff5252 !important;
  background: #fff8f8 !important;
}

.so-campo-obrigatorio fieldset {
  border-color: #ff5252 !important;
}

.so-form--readonly .v-input {
  pointer-events: none;
}

.so-form--readonly .v-input__slot {
  background: #f5f5f5 !important;
}

.so-item-descricao {
  white-space: normal !important;
  word-break: break-word;
  line-height: 1.25;
}

.so-item-fieldset {
  border: 0;
  margin: 0;
  padding: 0;
  min-width: 0;
}

@media (max-width: 1200px) {
  .so-topbar {
    gap: 8px;
    padding: 8px 12px;
  }

  .so-topbar__title {
    font-size: 1rem;
  }

  .so-topbar__btn {
    width: 124px;
    min-width: 124px !important;
    flex-basis: 124px;
  }

  .so-topbar__btn--auto-width {
    width: auto !important;
    min-width: 0 !important;
    flex-basis: auto;
  }
}

@media (max-width: 960px) {
  .so-topbar {
    grid-template-columns: auto 1fr;
    grid-template-areas:
      "close title"
      "actions actions";
  }

  .so-topbar__btn--close {
    grid-area: close;
  }

  .so-topbar__title {
    grid-area: title;
    justify-content: flex-start;
  }

  .so-topbar__actions {
    grid-area: actions;
  }
}
</style>

<style>
/* BUGFIX (a pedido do usuário): destaque em vermelho da mensagem de validação de campos calculados
   e desabilitados (ex.: KV "deve ser maior que zero"). Precisa ficar num <style> SEM "scoped" porque
   o texto da mensagem (.v-messages__message) é renderizado pelo componente interno VMessages do
   Vuetify, um filho de verdade — CSS escopado desta SFC (mesmo com ::v-deep, que não funcionou aqui)
   não alcança esse nó. A classe "so-erro-forcado" é aplicada via :class no próprio campo (dentro do
   <style scoped>, ver comentário lá) só quando a mensagem de erro dele estiver realmente ativa, então
   esta regra global não vaza para nada fora desse caso. Reaproveitável em qualquer outro campo
   calculado/desabilitado com validação, não só o KV. */
.so-erro-forcado .v-messages,
.so-erro-forcado .v-messages__message {
  color: #ff5252 !important;
  opacity: 1 !important;
}
</style>
